import {
  AppFile,
  BankReference,
  camelCaseToSentence,
  CreditApplicationFieldsValues,
  CreditApplicationReference,
  DynamicInvitedSignaturesFields,
  ExtractedTradeReference,
  formatMoney,
  getStatementRange,
  InvitedSignatures,
  renderTemplate,
  resolveSupplierRefDisplayValue,
} from '@dill/dill-shared';
import { LineChart } from '@mui/x-charts/LineChart';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { downloadFile, isHTML, openDillToast, toInlineHTML } from '../../../../utils/helpers';
import { DillButton } from '../../../general/Dill/DillButton';
import { Mail, Eye, Trash, Edit } from 'react-feather';
import {
  Timer,
  ArrowCircleUpRight,
  DownloadSimple,
  CheckCircle,
  ArrowCounterClockwise,
  PlusCircle,
  Warning,
  Flag,
  Copy,
} from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import ShowBankReferenceDetails from './ShowBankReferenceDetails';
import moment from 'moment';
import { AppPdfPreviewModal } from '../../../general/AppPdfPreviewModal/AppPdfPreviewModal';
import { AppImagePreviewModal } from '../../../general/AppImagePreviewModal/AppImagePreviewModal';
import UpdateFileModal from './UpdateFileModal';
import UpdateSignatureFileModal from './UpdateSignatureFileModal';
import TradeReferenceResponseModal from './TradeReferenceResponseModal';
import { DillDropdown } from '../../../general/Dill';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import PlaidSummary from './PlaidSummary';
import PlaidSummaryModal from './PlaidSummaryModal';
import { getSampleTin, getSamplSOS } from './middeskData';
import { getSamplePlaidMonthlyReport } from './plaidData';
import { TradeReferenceHistory } from './TradeReferenceHistory';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import EmailReference from './EmailReference';
import BankReferenceResponseModal from './BankReferenceResponseModal';
import { SealQuestion } from '@phosphor-icons/react';
import { verifySOSMiddeskBusiness } from '../../../../redux/services/middeskService';
import {
  applyTaxExemptNotExempt,
  getSupplierCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import { getIpLocationFlag, getMiddeskSummary } from './creditApplicationSummaryUtils';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import AppDatePicker, { parseDateValue } from './AppDatePicker';
import { AppCheckBox } from '../../../general/AppCheckBox/AppCheckBox';
import StateDropDown from './StateDropDown';
// import { CreditAppFormFields } from './CreditAppFormFields';

const EDITABLE_FIELD_TYPES = new Set([
  'TEXT',
  'NUMBER',
  'CURRENCY',
  'TEXT_AREA',
  'DATE',
  'CHECK_BOX',
  'OPTION_FIELD',
  'STATE_DROPDOWN',
  'CHECKBOX_LIST',
]);

const LOCKED_FIELD_IDS = new Set([
  'customer-or-company-name',
  'first-name-personal',
  'last-name-personal',
  'email-personal',
]);

type NestedGroupedFormData = {
  [stepName: string]: {
    [groupStepPosition: number]: {
      [groupedPosition: number]: CreditApplicationFieldsValues[];
    };
  };
};

type FormDataItem = CreditApplicationFieldsValues & {
  conditionalVisibility?: { sourceFieldId: string; sourceOptionValues: string[] } | null;
};

type Props = {
  formData: FormDataItem[];
  onSendEmail?: (field: CreditApplicationFieldsValues[], indx: number) => void;
  onFollowUp?: (field: CreditApplicationFieldsValues[], indx: number) => void;
  onAddTradeReference?: (field: CreditApplicationFieldsValues[]) => void;
  loadingButton?: string;
  bankReference?: BankReference[] | null;
  onGetPlaidDetails?: (accessToken: string, type: 'balance' | 'statements', month: string) => void;
  onRefreshPlaidDetails?: () => void;
  plaidLoadingStatus?: string;
  inviteSignatures?: InvitedSignatures[];
  currentCreditApplicationId?: string;
  isPreview?: boolean;
  isRequest?: boolean;
  onUploadTradeReferenceResponse?: (data: {
    files: { value: File; id: string }[];
    text: { value: string; id: string } | null;
    fields: CreditApplicationFieldsValues[];
  }) => void;
  loadingTradeReferenceResponse?: string;
  tradeReferenceResponse?: Record<
    string,
    { textResponse?: string | null; files?: AppFile[] | null }
  > | null;
  onDeleteTradeReferenceFile?: (id: string, url: string) => void;
  isSignature?: boolean;
  signatureInvited?: InvitedSignatures | null;
  onSignCreditApplication?: (data: { dynamicFields: DynamicInvitedSignaturesFields[] }) => void;
  signing?: boolean;
  onSignNow?: (type: string) => void;
  userSignatureTypes?: string[];
  onChangeTradeRefDetails?: (
    type: 'email' | 'company',
    field: CreditApplicationFieldsValues
  ) => void;
  tradeRefenceHistory?: CreditApplicationReference[] | null;
  onEditSignatureEmail?: (sigDetails: InvitedSignatures) => void;
  onResendSignature?: (sigDetails: InvitedSignatures) => void;
  onRequestSignatureDetailsUpdate?: (sigDetails: InvitedSignatures) => void;
  onMakeSignatureRequired?: (sigDetails: InvitedSignatures) => void;
  applicationHistoryId?: string | null;
  isOnlyView?: boolean;
  signaturePageFieldIds?: string[] | null;
  signatureErrors?: string[];
  pendingSignedData?: Record<
    string,
    {
      dynamicFields: DynamicInvitedSignaturesFields[];
      signature: string;
      email: string;
      placeHolder: string;
      dateSigned: string;
      ipAddress?: string;
      location?: string;
    }
  >;
  isEditingData?: boolean;
  editableFieldValues?: CreditApplicationFieldsValues[];
  onEditFieldValue?: (uniqueId: string, value: unknown) => void;
  editFieldErrors?: Record<string, string>;
};

type PlaidSummaryType = {
  bankName: string;
  date: string;
  amount: number;
  numberOfTransactions: number;
  transactions: {
    name?: string | null;
    description?: string | null;
    date: string;
    amount: number;
    detailedCategory?: string | null;
  }[];
};

export interface PreviewCreditApplicationRef {
  validate: () => boolean;
  onSubmit: () => Record<string, string>;
}

export const PreviewCreditApplicationData = forwardRef<PreviewCreditApplicationRef, Props>(
  (
    {
      formData,
      onSendEmail,
      onFollowUp,
      onAddTradeReference,
      loadingButton,
      bankReference,
      onGetPlaidDetails,
      onRefreshPlaidDetails,
      plaidLoadingStatus = '',
      inviteSignatures,
      currentCreditApplicationId = '',
      isPreview = false,
      isRequest = false,
      onUploadTradeReferenceResponse,
      loadingTradeReferenceResponse,
      tradeReferenceResponse,
      onDeleteTradeReferenceFile,
      isSignature = false,
      signatureInvited,
      onSignCreditApplication,
      signing,
      onChangeTradeRefDetails,
      tradeRefenceHistory,
      onEditSignatureEmail,
      onResendSignature,
      onRequestSignatureDetailsUpdate,
      onMakeSignatureRequired,
      applicationHistoryId,
      isOnlyView = false,
      signaturePageFieldIds,
      onSignNow,
      userSignatureTypes,
      pendingSignedData,
      signatureErrors,
      isEditingData = false,
      editableFieldValues,
      onEditFieldValue,
      editFieldErrors,
    },
    ref
  ) => {
    const dispatch = useAppDispatch();
    const [attachmentUrl, setAttachmentUrl] = useState('');
    const [showPreviewPdf, setShowPreviewPdf] = useState(false);
    const [showPreviewImage, setShowPreviewImage] = useState(false);
    const [verifyingSOS, setVerifyingSOS] = useState(false);
    const [updateFileModal, setUpdateFileModal] = useState(false);
    const [updateSignatureFileModal, setUpdateSignatureFileModal] = useState(false);
    const [selectedInvitedSignatures, setSelectedInvitedSignatures] =
      useState<InvitedSignatures | null>(null);
    const [selectedInvitedSignatureId, setSelectedInvitedSignatureId] = useState<string | null>(
      null
    );
    const [selectedField, setSelectedField] = useState<CreditApplicationFieldsValues | null>(null);
    const [openTradeReferenceModal, setOpenTradeReferenceModal] = useState(false);
    const [openBankReferenceModal, setOpenBankReferenceModal] = useState(false);
    const [selectedBankReference, setSelectedBankReference] = useState<BankReference | null>(null);
    const [tradeReferenceManualResponse, setTradeReferenceManualResponse] = useState<{
      fields: CreditApplicationFieldsValues[];
      index: number;
    } | null>(null);
    const [selectedStatementMonth, setSelectedStatementMonth] = useState('');
    const [selectedTransactionSummary, setSelectedTransactionSummary] =
      useState<PlaidSummaryType | null>(null);
    const [openPlaidSummaryModal, setOpenPlaidSummaryModal] = useState(false);

    const { creditApplication } = useAppSelector((state) => state.creditApplications);
    const { user } = useAppSelector((state) => state.auth);
    const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
    const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } =
      useAppSelector((state) => state.supplierBranches);
    useEffect(() => {
      if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
        dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
      }
    }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);
    const previewCreditApplicationRef = useRef<PreviewCreditApplicationRef>(null);
    const companyStateAbbr =
      creditApplication?.fieldValues?.find((field) => field.id === 'company-state')?.value ?? null;

    useImperativeHandle(ref, () => ({
      validate: () => previewCreditApplicationRef.current?.validate() ?? true,
      onSubmit: () => previewCreditApplicationRef.current?.onSubmit() ?? {},
    }));

    const groupByStepAndPosition = (data: FormDataItem[]): NestedGroupedFormData => {
      const isFieldVisible = (field: FormDataItem): boolean => {
        if (!field.conditionalVisibility?.sourceFieldId) return true;
        const { sourceFieldId, sourceOptionValues } = field.conditionalVisibility;
        const sourceValue = String(data.find((f) => f.id === sourceFieldId)?.value ?? '');
        return sourceOptionValues.includes(sourceValue);
      };

      return data
        .filter((f) => f.fromField !== 'AUTOFILL_CHECKBOX')
        .filter(isFieldVisible)
        .filter(
          (f) =>
            isOnlyView ||
            isPreview ||
            isRequest ||
            (isSignature && f.fromField === 'INVITE_USER') ||
            f.fromField !== 'INVITE_USER'
        )
        .filter((f) => {
          if (!isSignature || !signaturePageFieldIds || signaturePageFieldIds.length === 0)
            return true;
          if (f.fromField === 'INVITE_USER') return true;
          return signaturePageFieldIds.includes(f.id);
        })
        .reduce<NestedGroupedFormData>((acc, item) => {
          const step = item.stepName || 'Unknown Step';
          const groupStep = item.groupStepPosition ?? 0;
          const grouped = item.groupedPosition ?? 0;

          if (!acc[step]) {
            acc[step] = {};
          }

          if (!acc[step][groupStep]) {
            acc[step][groupStep] = {};
          }

          if (!acc[step][groupStep][grouped]) {
            acc[step][groupStep][grouped] = [];
          }

          acc[step][groupStep][grouped].push(item);

          return acc;
        }, {});
    };

    const getMonthObjects = (): { name: string; value: string }[] => {
      const statementDateRange = getStatementRange({ monthsRange: 12 });
      const result: { name: string; value: string }[] = [];

      const start = moment(statementDateRange.start_date).startOf('month');
      const end = moment(statementDateRange.end_date).startOf('month');
      const monthDiff = end.diff(start, 'months');

      for (let i = 0; i <= monthDiff; i++) {
        const current = moment(end).subtract(i, 'months');
        result.push({
          name: current.format('MMMM YYYY'),
          value: current.add(1, 'months').format('YYYY-MM-DD'),
        });
      }

      return result;
    };

    const monthObjects = useMemo(() => getMonthObjects(), []);

    const groupedData = useMemo(() => groupByStepAndPosition(formData), [formData]);

    const handleDownloadFile = async (fileUrl: string, fileName: string) => {
      downloadFile(fileUrl ?? '', fileName);
    };

    const handlePreviewFile = (url: string, fileType?: string) => {
      setAttachmentUrl(url);
      if (fileType && fileType !== 'pdf') {
        setShowPreviewImage(true);
      } else {
        setShowPreviewPdf(true);
      }
    };

    const plaidMonthlyReportData = useMemo(() => {
      const realReport = creditApplication?.plaidMonthlyReport?.plaidReport ?? [];
      if (process.env.REACT_APP_ENVIRONMENT !== 'production') {
        const plaidBankNames = (bankReference ?? [])
          .filter((br) => br.connectionType === 'plaid')
          .map((br) => br.bankDetails?.bankName ?? '')
          .filter(Boolean);
        return getSamplePlaidMonthlyReport(plaidBankNames);
      }
      return realReport.filter(Boolean) as NonNullable<(typeof realReport)[number]>[];
    }, [creditApplication?.plaidMonthlyReport?.plaidReport, bankReference]);

    const middeskReportProduction = useMemo(() => {
      return getMiddeskSummary(creditApplication?.middeskReport?.report);
    }, [creditApplication?.middeskReport?.report]);

    const middeskReportTestData = useMemo(() => {
      if (process.env.REACT_APP_ENVIRONMENT === 'production') return null;
      const companyName = formData.find(
        (item) => item.id === 'customer-or-company-name' && item.value !== ''
      );
      const companyAddress = formData.find(
        (item) => item.id === 'company-address' && item.value !== ''
      );
      const companyCity = formData.find((item) => item.id === 'company-city' && item.value !== '');
      const companyState = formData.find(
        (item) => item.id === 'company-state' && item.value !== ''
      );
      const companyZip = formData.find((item) => item.id === 'company-zip' && item.value !== '');
      let address = {};
      if (companyAddress?.value) {
        address = {
          ...address,
          address: companyAddress.value,
        };
      }
      if (companyCity?.value) {
        address = {
          ...address,
          city: companyCity.value,
        };
      }
      if (companyState?.value) {
        address = {
          ...address,
          state: companyState.value,
        };
      }
      if (companyZip?.value) {
        address = {
          ...address,
          zip: companyZip.value,
        };
      }
      const sosData = getSamplSOS(companyName?.value ?? '', address);
      const isEINItem = (item: { id: string; value?: unknown }) =>
        (item.id === 'company-ein' ||
          (item.id.includes('ein-number') && !item.id.includes('trade-reference'))) &&
        item.value !== '';

      const companyEIN =
        formData.find(isEINItem) ??
        formData.flatMap((item) => item.fieldValue ?? []).find(isEINItem);
      const tinData = getSampleTin(companyEIN?.value ?? '', companyName?.value ?? '');
      if (sosData && tinData) {
        return {
          ...sosData,
          ...tinData,
        };
      }
      return null;
    }, [formData]);

    // Sample data is a dev/staging fallback only; production shows nothing until a real report exists.
    const middeskReport = useMemo(
      () =>
        middeskReportProduction ||
        (process.env.REACT_APP_ENVIRONMENT !== 'production' ? middeskReportTestData : null),
      [middeskReportTestData, middeskReportProduction]
    );

    const handleCopyReferenceLink = async (emailValue?: string | null) => {
      if (!emailValue) {
        openDillToast({ type: 'ERROR', message: 'No reference email found.' });
        return;
      }
      const link = `${
        process.env.REACT_APP_FRONTEND_URL
      }/referenceCreditApplication?domainId=${encodeURIComponent(
        currentSupplier?.domainId ?? ''
      )}&creditApplicationId=${encodeURIComponent(
        currentCreditApplicationId
      )}&email=${encodeURIComponent(emailValue)}`;

      try {
        await navigator.clipboard.writeText(link);
        openDillToast({ type: 'SUCCESS', message: 'Reference link copied to clipboard.' });
      } catch (err) {
        openDillToast({ type: 'ERROR', message: 'Failed to copy link.' });
      }
    };

    const handleVerifySOS = async () => {
      if (!currentSupplier) return;
      if (creditApplication?.middeskBusinessId) {
        openDillToast({
          type: 'SUCCESS',
          message: 'SoS and EIN verification was already requested for this application.',
        });
        return;
      }
      setVerifyingSOS(true);
      const results = await dispatch(
        verifySOSMiddeskBusiness({
          supplierId: currentSupplier?.id ?? '',
          creditApplicationId: creditApplication?.id || '',
        })
      );
      if (results.type.includes('fulfilled')) {
        await dispatch(
          getSupplierCreditApplication({
            supplierId: currentSupplier?.id || '',
            creditApplicationId: creditApplication?.id || '',
            hashedType: 'encrypted',
            fetchType: 'detailed',
            skipLoading: 'true',
          })
        );
        openDillToast({
          type: 'SUCCESS',
          message:
            'Successfully requested SoS and EIN verification. Please reload the page to see results.',
        });
        setVerifyingSOS(false);
      } else {
        setVerifyingSOS(false);
        openDillToast({
          type: 'ERROR',
          message: `Failed to request for verification of SOS.`,
        });
      }
    };

    const wasRefreshedLastMonth = useMemo(() => {
      const refreshedAt = creditApplication?.plaidMonthlyReport?.refreshedAt;

      if (!refreshedAt) return null;

      const refreshedMoment = moment(refreshedAt);

      const secondOfCurrentMonth = moment().clone().date(2).endOf('day');

      return {
        showRefreshButton: refreshedMoment.isBefore(secondOfCurrentMonth),
        refreshedAt: refreshedMoment.format('MM/DD/YYYY'),
      };
    }, [creditApplication?.plaidMonthlyReport?.refreshedAt]);

    // Before submission, a blank signer just hasn't been filled in by the applicant yet; only on a
    // submitted application does a blank signer mean the signature was not required.
    const isAwaitingApplicant =
      !isRequest && ['DRAFT', 'SENT', 'OPENED'].includes(creditApplication?.status ?? '');
    const emptySignatureLabel = isAwaitingApplicant
      ? 'No signature provided'
      : 'Signature not required';

    const renderInvitedSignatures = (field: CreditApplicationFieldsValues) => {
      const signatures = inviteSignatures?.filter((sig) => sig.type === field.type);
      if (!signatures || signatures.length === 0)
        return <div className="text-xs text-gray-500">{emptySignatureLabel}</div>;
      const checkEmail = signatures.filter((sig) => sig.email.trim() === '');
      if (checkEmail.length > 0 && checkEmail.length === signatures.length) {
        return (
          <div className="w-full flex justify-between items-center">
            <div className="text-xs text-gray-500">{emptySignatureLabel}</div>
            {onMakeSignatureRequired &&
              creditApplication?.status !== 'DRAFT' &&
              creditApplication?.status !== 'SENT' &&
              creditApplication?.status !== 'OPENED' && (
                <DillButton
                  variant="ghost-blue"
                  label="Make signature required"
                  className="mr-2 font-semibold"
                  onClick={() => {
                    onMakeSignatureRequired(signatures[0]);
                  }}
                />
              )}
          </div>
        );
      }
      const sigType = signatures[0];
      const amountOfCreditRequested = formData.find(
        (item) => item.id === 'amount-of-credit-requested'
      )?.value;

      const minSignatures = sigType.minimumNumberOfSignatures;
      const minCreditAmount = sigType.minimumCreditAmount;
      const hasMinDefined = minSignatures !== undefined;

      let isSignaturesRequired = true;
      let message = '';
      const filteredSignatures = formData.find((item) => item.id === sigType?.linkedFieldId);

      let template = null;
      if (filteredSignatures && filteredSignatures?.formTemplate) {
        const htmlVal = (sigType?.fields ?? []).reduce<Record<string, string>>((acc, fld) => {
          acc[fld?.id ?? ''] = fld.value;
          return acc;
        }, {});
        const updatedHtmlVal = {
          ...htmlVal,
          signature: sigType.signature
            ? `<img class="h-[20px] w-auto" src=${sigType.signature} alt="" />`
            : '',
          'signed-date': sigType.signature ? moment(sigType.dateSigned).format('MM-DD-YYYY') : '',
        };
        template = renderTemplate(filteredSignatures?.formTemplate ?? '', updatedHtmlVal);
      }

      if (sigType.type === 'GUARANTOR' || sigType.type === 'CORPORATE_GUARANTOR') {
        const creditAmount = Number(amountOfCreditRequested);
        if (minCreditAmount !== undefined && amountOfCreditRequested !== undefined) {
          isSignaturesRequired = creditAmount >= minCreditAmount;
        } else if (hasMinDefined) {
          isSignaturesRequired = minSignatures === signatures.length;
          if (minSignatures > 0 && minSignatures < signatures.length) {
            message = `${minSignatures} signature${minSignatures > 1 ? 's' : ''} is required`;
          }
        } else {
          isSignaturesRequired = false;
        }
      } else {
        if (hasMinDefined) {
          isSignaturesRequired = minSignatures === signatures.length;
          if (minSignatures > 0 && minSignatures < signatures.length) {
            message = `${minSignatures} signature${minSignatures > 1 ? 's' : ''} is required`;
          }
        } else {
          isSignaturesRequired = false;
        }
      }
      // Invited under a conditionalRequired that wasn't met (e.g. the Personal Guarantor unless
      // Business Type is "LLC (Single-member)") — optional, see InvitedSignaturesSchema.isOptional.
      if (signatures.every((sig) => sig.isOptional)) {
        isSignaturesRequired = false;
      }

      const signatureWithEmail = signatures.filter((sig) => sig.email.trim() !== '');

      return (
        <div>
          {template && (
            <div>
              {isHTML(template) ? (
                <div dangerouslySetInnerHTML={{ __html: template }} />
              ) : (
                <p>{template}</p>
              )}
            </div>
          )}
          {isRequest ? (
            <div className="flex flex-col gap-2">
              {signatureWithEmail.map((sig) =>
                sig.signature ? (
                  <div
                    key={`${sig.email}_${sig.placeHolder}`}
                    className="w-full flex flex-col border rounded-lg overflow-hidden">
                    <div className="grid grid-cols-2 gap-1 md:gap-2">
                      <div className="flex flex-col items-start gap-1 p-2">
                        <div className="text-xs font-semibold">{sig.placeHolder ?? ''}</div>
                        <div className="text-xs">{sig.email}</div>
                      </div>
                      <div className="flex flex-col items-start gap-1 p-2">
                        <div className="flex items-center gap-2">
                          <img
                            src={sig.signature}
                            alt="Signature"
                            className="h-[24px] w-auto object-contain"
                          />
                          <p className="text-[9px] text-gray-500">E-signed using Dill</p>
                        </div>
                        {(sig.dateSigned || sig.ipAddress || sig.location) && (
                          <div className="text-[10px] text-gray-500">
                            {sig.dateSigned && `Date Signed: ${sig.dateSigned}`}
                            {sig.location && ` at ${sig.location}`}
                            {sig.ipAddress && ` from IP Address ${sig.ipAddress}`}
                          </div>
                        )}
                      </div>
                    </div>
                    {(sig.fields ?? []).filter((f) => f.name && f.value != null && f.value !== '')
                      .length > 0 && (
                      <div className="grid grid-cols-2 gap-1 md:gap-2 border-t">
                        {(sig.fields ?? [])
                          .filter((f) => f.name && f.value != null && f.value !== '')
                          .map((f) => (
                            <div key={f.id} className="flex flex-col items-start gap-1 p-2">
                              <div className="text-xs font-semibold">
                                {f.name}
                                {f.isRequired && <span className="text-red-500">*</span>}
                              </div>
                              <div className="text-xs">
                                {f.value instanceof File
                                  ? f.value.name
                                  : typeof f.value === 'object' && f.value !== null
                                  ? (f.value as { name?: string })?.name ?? ''
                                  : String(f.value)}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    key={`${sig.email}_${sig.placeHolder}`}
                    className="w-full flex gap-1 md:gap-2">
                    <div className="w-full text-xs">{sig.placeHolder}</div>
                    <div className="w-full text-xs text-gray-500">{sig.email}</div>
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="flex flex-col mt-2">
              {message && <p className="text-red-500 text-xs">{message}</p>}
              <UpdateSignatureFileModal
                open={updateSignatureFileModal}
                handleClose={() => setUpdateSignatureFileModal(false)}
                selectedInvitedSignatureId={selectedInvitedSignatureId}
                selectedInvitedSignatures={selectedInvitedSignatures}
                creditApplicationId={currentCreditApplicationId}
              />
              <AppImagePreviewModal
                open={showPreviewImage}
                handleClose={() => {
                  setShowPreviewImage(false);
                }}
                title="Credit App Preview"
                url={attachmentUrl}
              />
              <AppPdfPreviewModal
                open={showPreviewPdf}
                handleClose={() => {
                  setShowPreviewPdf(false);
                  setAttachmentUrl('');
                }}
                invoice={''}
                pdfType="CREDIT_APPLICATION"
                pdfList={[
                  {
                    name: 'Credit Application',
                    url: attachmentUrl,
                    date: new Date(),
                  },
                ]}
                handleDownloadPdf={() => {}}
                isSigned={false}
                mode="Supplier"
              />
              {signatures.map((signature, index) => {
                const signatureFields = signature.fields || [];
                if (signatureFields.length === 0) return null;
                return (
                  <div
                    key={`signature_id-${index}`}
                    className={`flex flex-col ${
                      index !== signatures.length - 1 ? 'border-b mb-2 pb-2' : ''
                    }`}>
                    {signature.email.trim() === '' ? (
                      <div className={`flex w-full flex-col border rounded-lg`}>
                        <div className="flex w-full gap-1 md:gap-2">
                          <div className="flex flex-col w-full items-start gap-1 p-2">
                            <div className="text-xs font-semibold">
                              {signature?.placeHolder ?? ''}
                              {isSignaturesRequired && <span className="text-red-500">*</span>}
                            </div>
                            <div className="w-full flex justify-between items-center">
                              <div className="text-xs text-gray-500">{emptySignatureLabel}</div>
                              {onMakeSignatureRequired &&
                                creditApplication?.status !== 'DRAFT' &&
                                creditApplication?.status !== 'SENT' &&
                                creditApplication?.status !== 'OPENED' && (
                                  <DillButton
                                    variant="ghost-blue"
                                    label="Make signature required"
                                    className="mr-2 font-semibold"
                                    onClick={() => {
                                      onMakeSignatureRequired(signature);
                                    }}
                                  />
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col border rounded-lg">
                        {((!signature.signature &&
                          onResendSignature &&
                          creditApplication?.status !== 'DRAFT' &&
                          creditApplication?.status !== 'SENT' &&
                          creditApplication?.status !== 'OPENED') ||
                          (signature.signature &&
                            onRequestSignatureDetailsUpdate &&
                            creditApplication?.status !== 'DRAFT' &&
                            creditApplication?.status !== 'SENT' &&
                            creditApplication?.status !== 'OPENED')) && (
                          <div className="flex justify-end px-2 pt-2 gap-3">
                            {!signature.signature && onResendSignature && (
                              <div
                                className="flex items-center gap-1 md:gap-2 text-xs PRIMARY_500-CLR font-semibold cursor-pointer"
                                onClick={() => onResendSignature(signature)}>
                                <div className="text-xs">Resend Signature Request</div>
                                <ArrowCounterClockwise size={16} />
                              </div>
                            )}
                            {signature.signature && onRequestSignatureDetailsUpdate && (
                              <div
                                className="flex items-center gap-1 md:gap-2 text-xs PRIMARY_500-CLR font-semibold cursor-pointer"
                                onClick={() => onRequestSignatureDetailsUpdate(signature)}>
                                <div className="text-xs">Request For Update Signature Details</div>
                                <ArrowCounterClockwise size={16} />
                              </div>
                            )}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-1 md:gap-2">
                          <div className="flex flex-col items-start gap-1 p-2">
                            <div className="text-xs font-semibold">
                              {signature?.placeHolder ?? ''}
                              {isSignaturesRequired && <span className="text-red-500">*</span>}
                            </div>

                            <div className="flex items-center">
                              <div className="text-xs">{signature.email}</div>

                              {!signature.signature &&
                                creditApplication?.status !== 'DRAFT' &&
                                creditApplication?.status !== 'SENT' &&
                                creditApplication?.status !== 'OPENED' &&
                                onEditSignatureEmail && (
                                  <DillButton
                                    variant="ghost-blue"
                                    icon={<Edit size={14} />}
                                    className="ml-2"
                                    height={22}
                                    width={22}
                                    onClick={() => onEditSignatureEmail(signature)}
                                  />
                                )}
                            </div>
                          </div>

                          {signature.signature ? (
                            <div className="flex flex-col items-start gap-1 p-2">
                              <div className="flex gap-1 md:gap-2 items-center">
                                <img
                                  src={signature.signature}
                                  alt="Signature"
                                  className="h-[24px] w-auto object-contain"
                                />
                                <p className="text-[9px]">{`E-signed using Dill ${currentCreditApplicationId}__${index}__${new Date(
                                  signature?.dateSigned ?? ''
                                ).getTime()}`}</p>
                              </div>

                              <div className="text-[10px]">
                                Date Signed: {moment(signature.dateSigned).format('MM-DD-YYYY')}
                                {signature.location && ` at ${signature.location}`}
                                {signature.ipAddress && ` from IP Address ${signature.ipAddress}`}
                              </div>
                            </div>
                          ) : (
                            <div className="flex flex-col items-start gap-1 p-2">
                              <div className="text-xs font-semibold">Signature</div>
                              <div className="flex flex-col">
                                <div className="text-xs text-gray-500">No signature provided</div>
                              </div>
                            </div>
                          )}

                          {signatureFields.filter((fld) => fld.value).length > 0 && (
                            <div className="col-span-2 grid grid-cols-2 gap-1 md:gap-2 mt-2 w-full">
                              {signatureFields
                                .filter((fld) => fld.value)
                                .map((signatureField, ind) => (
                                  <div
                                    className="flex flex-col items-start gap-1 p-2"
                                    key={`signatureField-${ind}-${signatureField.id}`}>
                                    <div className="text-xs font-semibold">
                                      {signatureField.name}
                                      {signatureField.isRequired && (
                                        <span className="text-red-500">*</span>
                                      )}
                                    </div>

                                    {signatureField.value instanceof File ||
                                    typeof signatureField.value === 'object' ? (
                                      <div className="flex gap-1 md:gap-2 items-center">
                                        {isPreview && signatureField.value?.url && (
                                          <div className="flex items-center gap-1 md:gap-2">
                                            <DillButton
                                              variant="icon-default"
                                              width="25px"
                                              height="25px"
                                              onClick={() => {
                                                handleDownloadFile(
                                                  signatureField.value?.url ?? '',
                                                  signatureField.value?.name ?? ''
                                                );
                                              }}
                                              icon={
                                                <DownloadSimple color={COLORS.GREY_500} size={14} />
                                              }
                                            />

                                            {['pdf', 'jpg', 'jpeg', 'png'].includes(
                                              signatureField.value?.fileType
                                            ) && (
                                              <DillButton
                                                variant="icon-default"
                                                width="25px"
                                                height="25px"
                                                icon={<Eye color={COLORS.GREY_500} size={14} />}
                                                onClick={() =>
                                                  handlePreviewFile(
                                                    signatureField.value?.url ?? '',
                                                    signatureField?.value?.fileType ?? ''
                                                  )
                                                }
                                              />
                                            )}
                                          </div>
                                        )}

                                        <div
                                          className="text-xs"
                                          data-private={signatureField.isHashed ? true : false}>
                                          {signatureField?.value?.name ?? ''}
                                        </div>

                                        {isPreview && (
                                          <DillButton
                                            variant="ghost-blue"
                                            label="Upload"
                                            onClick={() => {
                                              setSelectedInvitedSignatures(signature);
                                              setSelectedInvitedSignatureId(signatureField.id);
                                              setUpdateSignatureFileModal(true);
                                            }}
                                          />
                                        )}
                                      </div>
                                    ) : (
                                      <div
                                        className="text-xs"
                                        data-private={signatureField.isHashed ? true : false}>
                                        {signatureField.value}
                                      </div>
                                    )}
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    };

    const findEditableField = (uniqueId: string): CreditApplicationFieldsValues | undefined => {
      for (const field of editableFieldValues ?? []) {
        if (field.uniqueId === uniqueId) return field;
        const nested = field.fieldValue?.find((fv) => fv.uniqueId === uniqueId);
        if (nested) return nested as unknown as CreditApplicationFieldsValues;
      }
      return undefined;
    };

    const formatEditEIN = (raw: string) => {
      const digits = raw.replace(/\D/g, '').slice(0, 9);
      return digits.length > 2 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : digits;
    };
    const formatEditSSN = (raw: string) => {
      const digits = raw.replace(/\D/g, '').slice(0, 9);
      if (digits.length > 5)
        return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
      if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return digits;
    };
    const formatEditPhone = (raw: string) => {
      const digits = raw.replace(/\D/g, '').slice(0, 10);
      if (digits.length > 6)
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      if (digits.length > 3) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      return digits;
    };

    const renderEditableFieldInput = (rawField: CreditApplicationFieldsValues) => {
      const field = findEditableField(rawField.uniqueId) ?? rawField;
      const currentValue = field.value;
      const handleChange = (value: unknown) => onEditFieldValue?.(field.uniqueId, value);
      const errorText = editFieldErrors?.[field.uniqueId] ?? '';

      switch (field.fromField) {
        case 'DATE':
          return (
            <AppDatePicker
              label={field.name}
              selectedDate={parseDateValue(currentValue)}
              isRequired={field.isRequired}
              showMonthYearPicker={field.datePickerFormat === 'MONTH_YEAR'}
              onDateSelected={(date) => handleChange(date)}
              errorText={errorText}
            />
          );
        case 'CHECK_BOX':
          return (
            <div className="my-1 flex flex-col w-full">
              <div className="flex items-center w-full">
                <AppCheckBox
                  className="cursor-pointer"
                  isChecked={typeof currentValue === 'boolean' ? currentValue : false}
                  onClick={(checked: boolean) => handleChange(checked)}
                />
                <div className="ml-2 text-sm">{field.name}</div>
              </div>
              {errorText && <p className="text-2xs mt-0.5 text-red-500">{errorText}</p>}
            </div>
          );
        case 'STATE_DROPDOWN':
          return (
            <StateDropDown
              isRequired={field.isRequired}
              label={field.name}
              value={typeof currentValue === 'string' ? currentValue : ''}
              onTextChange={(text) => handleChange(text)}
              errorText={errorText}
            />
          );
        default: {
          const displayValue =
            field.fromField === 'CURRENCY'
              ? typeof currentValue === 'number'
                ? String(currentValue)
                : ''
              : field.fromField === 'NUMBER'
              ? typeof currentValue === 'number'
                ? currentValue
                : ''
              : typeof currentValue === 'string'
              ? currentValue
              : '';
          return (
            <AppInputField
              id={field.id}
              autoComplete="new-password"
              label={field.name}
              inputType={
                field.fromField === 'TEXT_AREA'
                  ? 'textarea'
                  : field.fromField === 'NUMBER'
                  ? 'number'
                  : 'text'
              }
              value={displayValue}
              isRequired={field.isRequired}
              errorText={errorText}
              prefix={field.fromField === 'CURRENCY' ? '$' : undefined}
              placeholder={
                field.validationType === 'EIN'
                  ? 'XX-XXXXXXX'
                  : field.validationType === 'SSN'
                  ? 'XXX-XX-XXXX'
                  : field.validationType === 'PHONE'
                  ? 'XXX-XXX-XXXX'
                  : undefined
              }
              maxLength={
                field.validationType === 'EIN'
                  ? 10
                  : field.validationType === 'SSN'
                  ? 11
                  : field.validationType === 'PHONE'
                  ? 12
                  : field.maxLength
              }
              onTextChange={(text) => {
                if (field.fromField === 'CURRENCY') {
                  const stripped = text.replace(/[$,]/g, '');
                  if (!/^\d*\.?\d*$/.test(stripped)) return;
                  const numericValue = parseFloat(stripped);
                  handleChange(stripped === '' || isNaN(numericValue) ? null : numericValue);
                  return;
                }
                let formatted = text;
                if (field.fromField === 'TEXT') {
                  if (field.validationType === 'EIN') formatted = formatEditEIN(text);
                  else if (field.validationType === 'SSN') formatted = formatEditSSN(text);
                  else if (field.validationType === 'PHONE') formatted = formatEditPhone(text);
                }
                handleChange(
                  field.fromField === 'NUMBER' ? (text ? parseFloat(text) : null) : formatted
                );
              }}
              dataPrivate={field?.isHashed ? true : false}
            />
          );
        }
      }
    };

    const renderConditionalFieldData = (
      fields: CreditApplicationFieldsValues[],
      fieldId: string,
      title: string
    ) => {
      if (fields.length === 0) return null;

      // Separate non-grouped (groupName="") from grouped (groupName!=""), excluding document-only fields
      const visibleFields = fields.filter(
        (f) => f.fromField !== 'DOCUMENT_FIELD' && !(f as unknown as { hidden?: boolean }).hidden
      );
      if (visibleFields.length === 0) return null;
      const nonGroupedFields = visibleFields.filter((f) => !f.groupName);
      const hasGroupedFields = visibleFields.some((f) => !!f.groupName);

      // Build row arrays: fields sharing the same index+groupedPosition+groupName go in one flex row
      const buildRowGroups = (subset: CreditApplicationFieldsValues[]) =>
        Object.values(
          subset.reduce(
            (acc, field) => {
              const key = `${field.groupName ?? ''}-${field.index}-${field.groupedPosition}`;
              if (!acc[key]) {
                acc[key] = {
                  index: field.index,
                  groupedPosition: field.groupedPosition ?? 0,
                  groupName: field.groupName ?? '',
                  fields: [] as CreditApplicationFieldsValues[],
                };
              }
              acc[key].fields.push(field);
              return acc;
            },
            {} as Record<
              string,
              {
                index: number;
                groupedPosition: number;
                groupName: string;
                fields: CreditApplicationFieldsValues[];
              }
            >
          )
        );

      // Build grouped sections: groupName -> positions (instances) -> rows
      const groupedByName: Record<
        string,
        {
          index: number;
          groupedPosition: number;
          groupName: string;
          fields: CreditApplicationFieldsValues[];
        }[][]
      > = {};
      visibleFields
        .filter((f) => !!f.groupName)
        .forEach((f) => {
          if (!groupedByName[f.groupName!]) groupedByName[f.groupName!] = [];
        });
      for (const gn of Object.keys(groupedByName)) {
        const rows = buildRowGroups(visibleFields.filter((f) => f.groupName === gn));
        const positionMap: Record<number, typeof rows> = {};
        rows.forEach((row) => {
          const pos = row.groupedPosition;
          if (!positionMap[pos]) positionMap[pos] = [];
          positionMap[pos].push(row);
        });
        groupedByName[gn] = Object.values(positionMap);
      }

      const nonGroupedRows = buildRowGroups(nonGroupedFields);

      // Renders the fields in a single flex row
      const renderRow = (
        row: {
          index: number;
          groupedPosition: number;
          groupName: string;
          fields: CreditApplicationFieldsValues[];
        },
        rowKey: string
      ) => (
        <div key={rowKey} className="flex gap-2 md:gap-4 mb-2 md:mb-4">
          {row.fields.map((field, fieldIndex) => {
            const className =
              field.groupColumnType === 'THREE_COLUMNS'
                ? 'w-1/3'
                : field.groupColumnType === 'TWO_COLUMNS'
                ? 'w-1/2'
                : 'w-full';
            const isRowFileOrObject =
              field.value !== null &&
              (field.value instanceof File || typeof field.value === 'object');
            const rowHasValue =
              field.value !== null && field.value !== undefined && field.value !== '';
            return (
              <div
                key={`${field.uniqueId}-${fieldIndex}`}
                data-field-id={field.id}
                className="w-full flex">
                <div className={`flex flex-col ${className}`}>
                  {isEditingData &&
                  !LOCKED_FIELD_IDS.has(field.id) &&
                  EDITABLE_FIELD_TYPES.has(field.fromField ?? '') ? (
                    renderEditableFieldInput(field)
                  ) : field.fromField === 'CONDITIONAL_OPTION' ? (
                    <div>
                      <div className="text-xs font-semibold mb-1">
                        {field.name}
                        {field.isRequired && <span className="text-red-500">*</span>}
                      </div>
                      {isRowFileOrObject && (
                        <div className="flex flex-col">
                          <div className="text-xs">Yes</div>
                          {field?.secondaryText && (
                            <div className="text-xs font-semibold mt-1">
                              {field.secondaryText}
                              {field.isRequired && <span className="text-red-500">*</span>}
                            </div>
                          )}
                          <div className="flex gap-1 md:gap-2 items-center mt-1">
                            {isPreview && (
                              <div className="flex items-center gap-1 md:gap-2">
                                <DillButton
                                  variant="icon-default"
                                  width="25px"
                                  height="25px"
                                  onClick={() => {
                                    handleDownloadFile(
                                      field.value?.url ?? '',
                                      field.value?.name ?? ''
                                    );
                                  }}
                                  icon={<DownloadSimple color={COLORS.GREY_500} size={14} />}
                                />
                                {(field?.value?.fileType === 'pdf' ||
                                  field?.value?.fileType === 'jpg' ||
                                  field?.value?.fileType === 'jpeg' ||
                                  field?.value?.fileType === 'png') && (
                                  <DillButton
                                    variant="icon-default"
                                    width="25px"
                                    height="25px"
                                    icon={<Eye color={COLORS.GREY_500} size={14} />}
                                    onClick={() => {
                                      handlePreviewFile(
                                        field.value?.url ?? '',
                                        field?.value?.fileType
                                      );
                                    }}
                                  />
                                )}
                              </div>
                            )}
                            <div
                              className="text-xs w-fit truncate"
                              data-private={field?.isHashed ? true : false}>
                              {field?.value?.name ?? ''}
                            </div>
                            {isPreview && (
                              <DillButton
                                variant="ghost-blue"
                                label="Upload"
                                onClick={() => {
                                  setSelectedField(field);
                                  setUpdateFileModal(true);
                                }}
                              />
                            )}
                          </div>
                        </div>
                      )}
                      {field.value && typeof field.value === 'string' && field.value !== 'No' && (
                        <div className="flex flex-col">
                          <div className="text-xs">Yes</div>
                          {field?.secondaryText && (
                            <div className="text-xs font-semibold mt-1">
                              {field.secondaryText}
                              {field.isRequired && <span className="text-red-500">*</span>}
                            </div>
                          )}
                          <div
                            className="text-xs truncate"
                            data-private={field?.isHashed ? true : false}>
                            {field?.value ?? ''}
                          </div>
                        </div>
                      )}
                      {(field.value === null || field.value === 'No') && (
                        <div className="text-xs">No</div>
                      )}
                    </div>
                  ) : field.fromField === 'DATE' ? (
                    <div>
                      <div className="text-xs font-semibold mb-1">
                        {field.name}
                        {field.isRequired && <span className="text-red-500">*</span>}
                      </div>
                      <div className="text-xs">
                        {field?.value
                          ? moment(field.value, 'MM-DD-YYYY').format('MM-DD-YYYY')
                          : '-'}
                      </div>
                    </div>
                  ) : field.fromField === 'CHECK_BOX' ? (
                    <div>
                      <div className="text-xs font-semibold mb-1">
                        {field.name}
                        {field.isRequired && <span className="text-red-500">*</span>}
                      </div>
                      <div className="text-xs">{field?.value ? 'True' : 'False'}</div>
                    </div>
                  ) : field.fromField === 'INVITE_USER' && inviteSignatures ? (
                    <div>
                      <div className="text-xs font-semibold">
                        {field.name}
                        {field.isRequired && <span className="text-red-500">*</span>}
                      </div>
                      {renderInvitedSignatures(field)}
                    </div>
                  ) : (
                    <div>
                      {field.fromField !== 'DOCUMENT_FIELD' &&
                        field.fromField !== 'DYNAMIC_FORM_DOCUMENT' && (
                          <div className="text-xs font-semibold mb-1">
                            {field.name}
                            {field.isRequired && <span className="text-red-500">*</span>}
                          </div>
                        )}
                      {isRowFileOrObject ? (
                        <div className="flex gap-1 md:gap-2 items-center">
                          {isPreview && field.value?.url && (
                            <div className="flex items-center gap-1 md:gap-2">
                              <DillButton
                                variant="icon-default"
                                width="25px"
                                height="25px"
                                onClick={() => {
                                  handleDownloadFile(
                                    field.value?.url ?? '',
                                    field.value?.name ?? ''
                                  );
                                }}
                                icon={<DownloadSimple color={COLORS.GREY_500} size={14} />}
                              />
                              {(field?.value?.fileType === 'pdf' ||
                                field?.value?.fileType === 'jpg' ||
                                field?.value?.fileType === 'jpeg' ||
                                field?.value?.fileType === 'png') && (
                                <DillButton
                                  variant="icon-default"
                                  width="25px"
                                  height="25px"
                                  icon={<Eye color={COLORS.GREY_500} size={14} />}
                                  onClick={() => {
                                    handlePreviewFile(
                                      field.value?.url ?? '',
                                      field?.value?.fileType
                                    );
                                  }}
                                />
                              )}
                            </div>
                          )}
                          <div className="text-xs w-fit truncate">{field?.value?.name ?? ''}</div>
                          {isPreview && field.fromField === 'FILE' && (
                            <DillButton
                              variant="ghost-blue"
                              label="Upload"
                              onClick={() => {
                                setSelectedField(field);
                                setUpdateFileModal(true);
                              }}
                            />
                          )}
                        </div>
                      ) : isHTML(field.value) ? (
                        <div
                          className={className}
                          dangerouslySetInnerHTML={{ __html: field.value }}
                        />
                      ) : (
                        <div
                          className="text-xs truncate"
                          data-private={field?.isHashed ? true : false}>
                          {!rowHasValue
                            ? '-'
                            : field.fromField === 'CURRENCY'
                            ? formatMoney(field.value, (field as any).currencyDecimalPlaces ?? 2)
                            : resolveSupplierRefDisplayValue(
                                field.id,
                                field.value,
                                supplierBranches
                              )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {isPreview &&
                  field.id.includes('social-security') &&
                  process.env.REACT_APP_ENVIRONMENT !== 'production' && (
                    <div className="flex px-2 py-1 h-fit border SUCCESS_500-BORDER SUCCESS_100-BG SUCCESS_500-CLR items-center gap-1 rounded-md">
                      <CheckCircle size={14} weight="bold" />
                      <p className="text-xs font-semibold">SSN Verified</p>
                    </div>
                  )}
                {(field.id === 'company-ein' ||
                  (field.id.includes('ein-number') && !field.id.includes('trade-reference'))) &&
                  !creditApplication?.middeskBusinessId &&
                  creditApplication?.status !== 'OPENED' &&
                  creditApplication?.status !== 'SENT' &&
                  creditApplication?.status !== 'DRAFT' &&
                  process.env.REACT_APP_ENVIRONMENT === 'production' &&
                  isPreview && (
                    <DillButton
                      variant="secondary"
                      onClick={handleVerifySOS}
                      label="Verify EIN"
                      height="35px"
                      width="160px"
                      icon={<SealQuestion size={16} weight="fill" />}
                      isLoading={verifyingSOS}
                      isDisabled={verifyingSOS}
                    />
                  )}
                {(field.id === 'company-ein' ||
                  (field.id.includes('ein-number') && !field.id.includes('trade-reference'))) &&
                  middeskReport &&
                  isPreview && (
                    <div className="w-full mt-1 rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm ml-4">
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                        <CheckCircle size={13} weight="fill" className="GREY_500-CLR" />
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                          EIN Verification
                        </span>
                      </div>
                      <div className="flex items-start justify-between px-4 py-3 gap-4">
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-700">Tax ID</p>
                          {middeskReport.tinMatch ? (
                            <>
                              {middeskReport.tinMatch.message && (
                                <p className="text-[11px] GREY_500-CLR mt-0.5 leading-relaxed">
                                  {middeskReport.tinMatch.message}
                                </p>
                              )}
                              {middeskReport?.tin?.name && (
                                <p
                                  className="text-[11px] GREY_500-CLR mt-0.5"
                                  data-private={field?.isHashed ? true : false}>
                                  Found:{' '}
                                  <span className="font-semibold text-gray-700">
                                    {middeskReport.tin.name}
                                  </span>
                                </p>
                              )}
                            </>
                          ) : (
                            <p className="text-[11px] GREY_500-CLR mt-0.5 leading-relaxed">
                              EIN verification is pending processing
                            </p>
                          )}
                        </div>
                        {middeskReport.tinMatch && (
                          <span
                            className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                              middeskReport.tinMatch.status.toLowerCase().includes('success')
                                ? 'SUCCESS_100-BG SUCCESS_500-CLR SUCCESS_500-BORDER'
                                : 'ERROR_100-BG ERROR_500-CLR ERROR_500-BORDER'
                            }`}>
                            {middeskReport.tinMatch.status.toLowerCase().includes('success') ? (
                              <CheckCircle size={11} weight="bold" />
                            ) : (
                              <Warning size={11} weight="bold" />
                            )}
                            {middeskReport.tinMatch.label}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            );
          })}
        </div>
      );

      const modals = (
        <>
          <UpdateSignatureFileModal
            open={updateSignatureFileModal}
            handleClose={() => setUpdateSignatureFileModal(false)}
            selectedInvitedSignatureId={selectedInvitedSignatureId}
            selectedInvitedSignatures={selectedInvitedSignatures}
            creditApplicationId={currentCreditApplicationId}
          />
          <AppImagePreviewModal
            open={showPreviewImage}
            handleClose={() => {
              setShowPreviewImage(false);
            }}
            title="Credit App Preview"
            url={attachmentUrl}
          />
          <AppPdfPreviewModal
            open={showPreviewPdf}
            handleClose={() => {
              setShowPreviewPdf(false);
              setAttachmentUrl('');
            }}
            invoice={''}
            pdfType="CREDIT_APPLICATION"
            pdfList={[
              {
                name: 'Credit Application',
                url: attachmentUrl,
                date: new Date(),
              },
            ]}
            handleDownloadPdf={() => {}}
            isSigned={false}
            mode="Supplier"
          />
        </>
      );

      // All non-grouped: original single card with selected-option value as header
      if (!hasGroupedFields) {
        return (
          <div className="w-full mt-2 mb-2 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            {modals}
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                {title}
              </p>
            </div>
            <div className="p-3">
              {nonGroupedRows.map((row, idx) => renderRow(row, `ng-${idx}`))}
            </div>
          </div>
        );
      }

      // Mixed or all-grouped: non-grouped fields inline first, then each group in its own card
      return (
        <div className="w-full mt-2 mb-2">
          {modals}
          {nonGroupedRows.length > 0 && (
            <div className="mb-2">
              {nonGroupedRows.map((row, idx) => renderRow(row, `ng-${idx}`))}
            </div>
          )}
          {Object.entries(groupedByName).map(([groupName, positions]) => (
            <div
              key={groupName}
              className="w-full bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm mb-2">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                  {groupName}
                </p>
              </div>
              <div className="p-3">
                {positions.map((positionRows, posIdx) => (
                  <div
                    key={posIdx}
                    className={posIdx < positions.length - 1 ? 'border-b mb-2 pb-2' : ''}>
                    {positions.length > 1 && (
                      <p className="text-xs font-semibold text-gray-500 mb-2">
                        {groupName} {posIdx + 1}
                      </p>
                    )}
                    {positionRows.map((row, rowIdx) =>
                      renderRow(row, `g-${groupName}-${posIdx}-${rowIdx}`)
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    };

    const [isUpdatingTaxExempt, setIsUpdatingTaxExempt] = useState(false);

    const handleApplyTaxExemptNotExempt = async () => {
      if (!currentSupplier?.id || !creditApplication?.id) return;
      setIsUpdatingTaxExempt(true);
      const result = await dispatch(
        applyTaxExemptNotExempt({
          supplierId: currentSupplier.id,
          creditApplicationId: creditApplication.id,
        })
      );
      setIsUpdatingTaxExempt(false);
      openDillToast(
        result.type.endsWith('/fulfilled')
          ? { type: 'SUCCESS', message: 'Tax exempt answer updated to No.' }
          : { type: 'ERROR', message: 'Failed to update tax exempt answer.' }
      );
    };

    // Result of the tax exemption team's review of the customer's tax exemption file, shown
    // under the tax exempt answer (supplier view only).
    const renderTaxExemptReviewNotice = () => {
      const review = creditApplication?.taxExemptReview;
      if (!isPreview || !review) return null;
      if (review.status === 'PENDING') {
        return (
          <div className="order-last mt-1.5 w-fit rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-600">
            Sent to your tax exemption team for review
          </div>
        );
      }
      if (review.status === 'EXEMPT') {
        return (
          <div className="order-last mt-1.5 flex w-fit items-center gap-1 rounded-md border border-green-200 bg-green-50 px-2 py-1 text-[11px] font-semibold text-green-700">
            <CheckCircle size={12} weight="fill" className="shrink-0" />
            Confirmed by your tax exemption team
          </div>
        );
      }
      if (review.answerUpdatedToNo) {
        return (
          <div className="order-last mt-1.5 w-fit rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] text-gray-600">
            Updated to No after your tax exemption team&apos;s review
            {review.answerUpdatedBy ? ` (${review.answerUpdatedBy})` : ''}
          </div>
        );
      }
      return (
        <div className="order-last mt-1.5 flex flex-col gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] font-medium text-amber-800">
          <span>
            Your tax exemption team reviewed this file and confirmed that customer is not tax exempt
          </span>
          <DillButton
            variant="secondary"
            label={isUpdatingTaxExempt ? 'Updating...' : 'Update'}
            onClick={handleApplyTaxExemptNotExempt}
            isDisabled={isUpdatingTaxExempt}
            className="w-fit"
          />
        </div>
      );
    };

    const renderFieldData = (fields: CreditApplicationFieldsValues[]) => {
      const grouped = Object.values(
        fields.reduce((acc, field) => {
          const key = `${field.index}-${field.groupedPosition}`;
          if (!acc[key]) {
            acc[key] = {
              index: field.index,
              groupedPosition: field.groupedPosition ?? 0,
              fields: [],
            };
          }
          acc[key].fields.push(field);
          return acc;
        }, {} as Record<string, { index: number; groupedPosition: number; fields: CreditApplicationFieldsValues[] }>)
      );

      return grouped.map((group, index) => {
        if (group.fields.length === 0) return null;

        return (
          <div
            key={`${group.index}-${group.groupedPosition}-${index}`}
            className="flex gap-2 md:gap-4 mb-1 last:mb-0">
            {group.fields.map((field, fieldIndex) => {
              const className =
                field.groupColumnType === 'THREE_COLUMNS'
                  ? 'w-1/3'
                  : field.groupColumnType === 'TWO_COLUMNS'
                  ? 'w-1/2'
                  : 'w-full';
              const alwaysShow = ['INVITE_USER', 'DYNAMIC_FORM_DOCUMENT', 'CHECK_BOX'].includes(
                field.fromField ?? ''
              );
              const hasValue =
                field.value !== null && field.value !== undefined && field.value !== '';
              const isFieldEditableNow =
                isEditingData &&
                !LOCKED_FIELD_IDS.has(field.id) &&
                EDITABLE_FIELD_TYPES.has(field.fromField ?? '');
              if (!hasValue && !alwaysShow && !isFieldEditableNow) {
                const skipWithoutValue = [
                  'DOCUMENT_FIELD',
                  'CONDITIONAL_OPTION',
                  'CONDITIONAL_FORM',
                  'DYNAMIC_FORM_DOCUMENT',
                ];
                if (skipWithoutValue.includes(field.fromField ?? '')) return null;
                return (
                  <div
                    key={`${field.uniqueId}-${fieldIndex}`}
                    data-field-id={field.id}
                    className="w-full flex">
                    <div className={`flex flex-col w-full`}>
                      <div className="text-xs font-semibold mb-1">
                        {field.name}
                        {field.isRequired && <span className="text-red-500">*</span>}
                      </div>
                      <div className="flex items-center justify-between gap-1">
                        <div className="text-xs">-</div>
                        {/* An empty trade reference field can be filled in, like a filled one. */}
                        {field?.sendEmail &&
                          field.status !== 'Submitted' &&
                          onChangeTradeRefDetails && (
                            <DillButton
                              variant="ghost-blue"
                              icon={<Edit size={14} />}
                              height={22}
                              width={22}
                              onClick={() =>
                                onChangeTradeRefDetails(
                                  field.id.includes('email') ? 'email' : 'company',
                                  field
                                )
                              }
                            />
                          )}
                      </div>
                    </div>
                  </div>
                );
              }
              if (!hasValue && !alwaysShow && isFieldEditableNow) {
                return (
                  <div
                    key={`${field.uniqueId}-${fieldIndex}`}
                    data-field-id={field.id}
                    className="w-full flex">
                    <div className={`flex flex-col w-full`}>{renderEditableFieldInput(field)}</div>
                  </div>
                );
              }
              let template = '';
              if (field.fromField === 'DYNAMIC_FORM_DOCUMENT') {
                const fieldSignature = creditApplication?.fieldValues.find(
                  (f) => f.id === field.populateFieldSignatureId
                );
                const invitedSignature = (creditApplication?.invitedSignatures ?? []).find(
                  (s) => s.type === fieldSignature?.type
                );
                let signatureValue = {};
                if (invitedSignature) {
                  const sigVal = (invitedSignature?.fields ?? []).reduce<Record<string, string>>(
                    (acc2, fld) => {
                      acc2[
                        fld?.id &&
                        invitedSignature?.type &&
                        fld?.id.includes(invitedSignature?.type?.toLowerCase())
                          ? fld?.id
                          : fld?.id
                          ? `${invitedSignature.type?.toLowerCase()}-${fld?.id}`
                          : ''
                      ] = fld.value;
                      return acc2;
                    },
                    {}
                  );
                  signatureValue = {
                    ...sigVal,
                    signature: invitedSignature.signature
                      ? `<img style="height:30px;width:auto;display:block;"  src="${invitedSignature.signature}" alt="signature" />`
                      : '',
                    'signed-date': invitedSignature.dateSigned
                      ? moment(invitedSignature.dateSigned).format('MM-DD-YYYY')
                      : '',
                  };
                }
                const allFormDataVal = formData.reduce<
                  Record<string, string | number | boolean | null>
                >((acc, fld) => {
                  if (typeof fld.value !== 'object' || fld.value === null) {
                    acc[fld.id] = fld.value as string | number | boolean | null;
                  }
                  return acc;
                }, {});
                const htmlVal = (field?.fieldValue ?? []).reduce<
                  Record<string, string | number | boolean | null>
                >((acc, fld) => {
                  if (
                    fld.fromField === 'DATE' &&
                    fld.defaultValue?.toLowerCase().trim() === 'current' &&
                    (fld.value === null || fld.value === undefined || fld.value === '')
                  ) {
                    acc[fld.id] = moment().format('MM/DD/YYYY');
                  } else {
                    acc[fld.id] = fld.value;
                  }
                  return acc;
                }, {});
                const autoPopulateVal: Record<string, string | number | boolean | null> = {};
                if (field.autoPopulateFieldIds) {
                  field.autoPopulateFieldIds
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .forEach((autoId) => {
                      const autoField =
                        formData.find((f) => f.id === autoId) ??
                        creditApplication?.fieldValues.find((f) => f.id === autoId);
                      if (autoField !== undefined) {
                        autoPopulateVal[autoId] = autoField.value as
                          | string
                          | number
                          | boolean
                          | null;
                      }
                    });
                }
                const updatedHtmlVal = {
                  ...allFormDataVal,
                  ...htmlVal,
                  ...autoPopulateVal,
                  ...signatureValue,
                };
                const renderedTemplate = renderTemplate(field?.formTemplate ?? '', updatedHtmlVal);
                template = renderedTemplate;
              }
              return (
                <div
                  key={`${field.uniqueId}-${fieldIndex}`}
                  data-field-id={field.id}
                  className="w-full flex min-w-0">
                  <div className={`flex flex-col w-full min-w-0`}>
                    {field.id === 'tax-exempt-choice' && renderTaxExemptReviewNotice()}
                    {isEditingData &&
                    !LOCKED_FIELD_IDS.has(field.id) &&
                    EDITABLE_FIELD_TYPES.has(field.fromField ?? '') ? (
                      renderEditableFieldInput(field)
                    ) : field.fromField === 'CONDITIONAL_OPTION' ? (
                      <div>
                        <div className="text-xs font-semibold mb-1">
                          {field.name}
                          {field.isRequired && <span className="text-red-500">*</span>}
                        </div>
                        {(field.value instanceof File || typeof field.value === 'object') && (
                          <div className="flex flex-col">
                            <div className="text-xs">Yes</div>
                            {field?.secondaryText && (
                              <div className="text-xs font-semibold mt-1">
                                {field.secondaryText}
                                {field.isRequired && <span className="text-red-500">*</span>}
                              </div>
                            )}
                            <div className="flex gap-1 md:gap-2 items-center mt-1">
                              {isPreview && (
                                <div className="flex items-center gap-1 md:gap-2">
                                  <DillButton
                                    variant="icon-default"
                                    width="25px"
                                    height="25px"
                                    onClick={() => {
                                      handleDownloadFile(
                                        field.value?.url ?? '',
                                        field.value?.name ?? ''
                                      );
                                    }}
                                    icon={<DownloadSimple color={COLORS.GREY_500} size={14} />}
                                  />
                                  {(field?.value?.fileType === 'pdf' ||
                                    field?.value?.fileType === 'jpg' ||
                                    field?.value?.fileType === 'jpeg' ||
                                    field?.value?.fileType === 'png') && (
                                    <DillButton
                                      variant="icon-default"
                                      width="25px"
                                      height="25px"
                                      icon={<Eye color={COLORS.GREY_500} size={14} />}
                                      onClick={() => {
                                        handlePreviewFile(
                                          field.value?.url ?? '',
                                          field?.value?.fileType
                                        );
                                      }}
                                    />
                                  )}
                                </div>
                              )}
                              <div className="text-xs truncate">{field?.value?.name ?? ''}</div>
                              {isPreview && (
                                <DillButton
                                  variant="ghost-blue"
                                  label="Upload"
                                  onClick={() => {
                                    setSelectedField(field);
                                    setUpdateFileModal(true);
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        )}
                        {field.value && typeof field.value === 'string' && field.value !== 'No' && (
                          <div className="flex flex-col">
                            <div className="text-xs">Yes</div>
                            {field?.secondaryText && (
                              <div className="text-xs font-semibold mt-1">
                                {field.secondaryText}
                                {field.isRequired && <span className="text-red-500">*</span>}
                              </div>
                            )}
                            <div className="text-xs truncate">{field?.value ?? ''}</div>
                          </div>
                        )}
                        {(field.value === null || field.value === 'No') && (
                          <div className="text-xs">No</div>
                        )}
                      </div>
                    ) : field.fromField === 'CONDITIONAL_FORM' ? (
                      <div>
                        <div className="text-xs font-semibold mb-1">
                          {field.name}
                          {field.isRequired && <span className="text-red-500">*</span>}
                        </div>
                        {field.value &&
                          (field.value instanceof File || typeof field.value === 'object') && (
                            <div className="flex flex-col">
                              <div className="text-xs">Yes</div>
                              {field?.secondaryText && field.value !== null && (
                                <div className="text-xs font-semibold mt-1">
                                  {field.secondaryText}
                                  {field.isRequired && <span className="text-red-500">*</span>}
                                </div>
                              )}
                              <div className="flex gap-1 md:gap-2 items-center mt-1">
                                {isPreview && (
                                  <div className="flex items-center gap-1 md:gap-2">
                                    <DillButton
                                      variant="icon-default"
                                      width="25px"
                                      height="25px"
                                      onClick={() => {
                                        handleDownloadFile(
                                          field.value?.url ?? '',
                                          field.value?.name ?? ''
                                        );
                                      }}
                                      icon={<DownloadSimple color={COLORS.GREY_500} size={14} />}
                                    />
                                    {(field?.value?.fileType === 'pdf' ||
                                      field?.value?.fileType === 'jpg' ||
                                      field?.value?.fileType === 'jpeg' ||
                                      field?.value?.fileType === 'png') && (
                                      <DillButton
                                        variant="icon-default"
                                        width="25px"
                                        height="25px"
                                        icon={<Eye color={COLORS.GREY_500} size={14} />}
                                        onClick={() => {
                                          handlePreviewFile(
                                            field.value?.url ?? '',
                                            field?.value?.fileType
                                          );
                                        }}
                                      />
                                    )}
                                  </div>
                                )}
                                <div className="text-xs truncate">{field?.value?.name ?? ''}</div>
                                {isPreview && (
                                  <DillButton
                                    variant="ghost-blue"
                                    label="Upload"
                                    onClick={() => {
                                      setSelectedField(field);
                                      setUpdateFileModal(true);
                                    }}
                                  />
                                )}
                              </div>
                            </div>
                          )}
                        {field.value && typeof field.value === 'string' && field.value !== 'No' && (
                          <div className="flex flex-col">
                            <div className="text-xs">Yes</div>
                            {field?.secondaryText && field.value !== null && (
                              <div className="text-xs font-semibold mt-1">
                                {field.secondaryText}
                                {field.isRequired && <span className="text-red-500">*</span>}
                              </div>
                            )}
                            <div className="text-xs truncate">{field?.value ?? ''}</div>
                          </div>
                        )}
                        {field.value === 'No' && (
                          <div className="text-xs truncate">{field.value}</div>
                        )}
                        {!field.value && field.secondaryValue && (
                          <div className="text-xs truncate">{field.secondaryValue}</div>
                        )}
                      </div>
                    ) : field.fromField === 'DATE' ? (
                      <div>
                        <div className="text-xs font-semibold mb-1">
                          {field.name}
                          {field.isRequired && <span className="text-red-500">*</span>}
                        </div>
                        <div className="text-xs">
                          {field?.value
                            ? moment(field.value, 'MM-DD-YYYY').format('MM-DD-YYYY')
                            : ''}
                        </div>
                      </div>
                    ) : field.fromField === 'DYNAMIC_FORM_DOCUMENT' ? (
                      <div className="w-full">
                        {template && isHTML(template) ? (
                          <div
                            className={className}
                            dangerouslySetInnerHTML={{ __html: template }}
                          />
                        ) : (
                          <p>{template}</p>
                        )}
                        {field?.fieldValue &&
                          field.fieldValue.some(
                            (f) => !(f as unknown as { hidden?: boolean }).hidden
                          ) && (
                            <div className="mt-2">
                              {renderConditionalFieldData(
                                field.fieldValue,
                                field.uniqueId,
                                'Details'
                              )}
                            </div>
                          )}
                      </div>
                    ) : field.fromField === 'CHECK_BOX' ? (
                      <div>
                        <div className="text-xs font-semibold mb-1">
                          {field.name}
                          {field.isRequired && <span className="text-red-500">*</span>}
                        </div>
                        <div className="text-xs">{field?.value ? 'True' : 'False'}</div>
                      </div>
                    ) : field.fromField === 'INVITE_USER' && isSignature ? (
                      (() => {
                        const fieldType = field.type ?? '';
                        const pending = pendingSignedData?.[fieldType];
                        const canSign =
                          onSignNow &&
                          (!userSignatureTypes || userSignatureTypes.includes(fieldType));
                        if (!pending && !canSign) return null;

                        const linkedId =
                          (field as any).linkedFieldId ?? signatureInvited?.linkedFieldId;
                        const findField = formData.find((f) => f.id === linkedId);
                        const dataVal = findField
                          ? renderTemplate(findField.formTemplate ?? '', {})
                          : null;

                        return (
                          <>
                            {dataVal && (
                              <div>
                                {isHTML(dataVal) ? (
                                  <div dangerouslySetInnerHTML={{ __html: dataVal }} />
                                ) : (
                                  <p>{dataVal}</p>
                                )}
                              </div>
                            )}
                            <div>
                              <div className="text-xs font-semibold ml-2">
                                {field.name}
                                {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                              </div>
                              {pending ? (
                                (() => {
                                  const filledFields = pending.dynamicFields.filter(
                                    (f) => f.value != null && f.value !== ''
                                  );
                                  return (
                                    <div className="w-full flex flex-col border rounded-lg overflow-hidden mt-2">
                                      <div className="flex justify-end px-2 pt-2">
                                        <DillButton
                                          label="Edit"
                                          height={24}
                                          variant="secondary"
                                          onClick={() => onSignNow?.(fieldType)}
                                        />
                                      </div>
                                      <div className="grid grid-cols-2 gap-1 md:gap-2">
                                        <div className="flex flex-col items-start gap-1 p-2">
                                          <div className="text-xs font-semibold">
                                            {pending.placeHolder}
                                          </div>
                                          <div className="text-xs">{pending.email}</div>
                                        </div>
                                        <div className="flex flex-col items-start gap-1 p-2">
                                          {pending.signature && (
                                            <div className="flex items-center gap-2">
                                              <img
                                                src={pending.signature}
                                                alt="Signature"
                                                className="h-[24px] w-auto object-contain"
                                              />
                                              <p className="text-[9px] text-gray-500">
                                                E-signed using Dill
                                              </p>
                                            </div>
                                          )}
                                          <div className="text-[10px] text-gray-500">
                                            {`Date Signed: ${pending.dateSigned}`}
                                            {pending.location && ` at ${pending.location}`}
                                            {pending.ipAddress &&
                                              ` from IP Address ${pending.ipAddress}`}
                                          </div>
                                        </div>
                                      </div>
                                      {filledFields.length > 0 && (
                                        <div className="grid grid-cols-2 gap-1 md:gap-2 border-t">
                                          {filledFields.map((f) => (
                                            <div
                                              key={f.id}
                                              className="flex flex-col items-start gap-1 p-2">
                                              <div className="text-xs font-semibold">
                                                {f.name}
                                                {f.isRequired && (
                                                  <span className="text-red-500">*</span>
                                                )}
                                              </div>
                                              <div className="text-xs">
                                                {f.value instanceof File
                                                  ? f.value.name
                                                  : typeof f.value === 'object' && f.value !== null
                                                  ? (f.value as { name?: string })?.name ?? ''
                                                  : String(f.value)}
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()
                              ) : (
                                <div className="mt-2 px-2">
                                  <DillButton
                                    label="Sign Now"
                                    variant="primary"
                                    onClick={() => onSignNow?.(fieldType)}
                                    isLoading={!!signing}
                                    isDisabled={!!signing}
                                    width="200px"
                                  />
                                  {signatureErrors?.includes(fieldType) && (
                                    <p className="text-xs text-red-500 mt-1">
                                      Signature required before submitting.
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        );
                      })()
                    ) : field.fromField === 'INVITE_USER' && inviteSignatures && !isSignature ? (
                      <>
                        {(() => {
                          const findField = formData.find(
                            (f) => f.id === signatureInvited?.linkedFieldId
                          );
                          if (findField) {
                            const dataVal = renderTemplate(findField?.formTemplate ?? '', {});
                            return (
                              <div>
                                {dataVal && (
                                  <div>
                                    {isHTML(dataVal) ? (
                                      <div dangerouslySetInnerHTML={{ __html: dataVal }} />
                                    ) : (
                                      <p>{dataVal}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}
                        <div>
                          <div className="text-xs font-semibold">
                            {field.name}
                            {field.isRequired && <span className="text-red-500">*</span>}
                          </div>
                          {renderInvitedSignatures(field)}
                        </div>
                      </>
                    ) : field.fromField === 'CONDITIONAL_SELECTED_FIELD' ? (
                      <div>
                        <div className="text-xs font-semibold mb-1">
                          {isHTML(field.name) ? (
                            <span dangerouslySetInnerHTML={{ __html: toInlineHTML(field.name) }} />
                          ) : (
                            field.name
                          )}
                          {field.isRequired && <span className="text-red-500">*</span>}
                        </div>
                        <div className="text-xs truncate">
                          {resolveSupplierRefDisplayValue(field.id, field.value, supplierBranches)}
                        </div>
                        {field?.fieldValue && (
                          <div className="mt-2">
                            {renderConditionalFieldData(
                              field.fieldValue,
                              field.uniqueId,
                              field.value
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        {field.fromField !== 'DOCUMENT_FIELD' && (
                          <div className="text-xs font-semibold mb-1">
                            {field.name}
                            {field.isRequired && <span className="text-red-500">*</span>}
                          </div>
                        )}
                        {field.value !== null &&
                        (field.value instanceof File || typeof field.value === 'object') ? (
                          <div className="flex gap-1 md:gap-2 items-center">
                            {isPreview && field.value?.url && (
                              <div className="flex items-center gap-1 md:gap-2">
                                <DillButton
                                  variant="icon-default"
                                  width="25px"
                                  height="25px"
                                  onClick={() => {
                                    handleDownloadFile(
                                      field.value?.url ?? '',
                                      field.value?.name ?? ''
                                    );
                                  }}
                                  icon={<DownloadSimple color={COLORS.GREY_500} size={14} />}
                                />
                                {(field?.value?.fileType === 'pdf' ||
                                  field?.value?.fileType === 'jpg' ||
                                  field?.value?.fileType === 'jpeg' ||
                                  field?.value?.fileType === 'png') && (
                                  <DillButton
                                    variant="icon-default"
                                    width="25px"
                                    height="25px"
                                    icon={<Eye color={COLORS.GREY_500} size={14} />}
                                    onClick={() => {
                                      handlePreviewFile(
                                        field.value?.url ?? '',
                                        field?.value?.fileType
                                      );
                                    }}
                                  />
                                )}
                              </div>
                            )}
                            <div className="text-xs truncate">{field?.value?.name ?? ''}</div>
                            {isPreview && field.fromField === 'FILE' && (
                              <DillButton
                                variant="ghost-blue"
                                label="Upload"
                                onClick={() => {
                                  setSelectedField(field);
                                  setUpdateFileModal(true);
                                }}
                              />
                            )}
                          </div>
                        ) : isHTML(field.value) ? (
                          <div
                            className={className}
                            dangerouslySetInnerHTML={{ __html: field.value }}
                          />
                        ) : (
                          <div>
                            {field.id.includes('trade-reference-company-email') ? (
                              <EmailReference
                                field={field}
                                onChangeTradeRefDetails={onChangeTradeRefDetails}
                              />
                            ) : (
                              <div className="w-full min-w-0">
                                <div className="flex gap-1 items-center min-w-0">
                                  {creditApplication?.isFraudulent &&
                                    isPreview &&
                                    field.id.includes('email-personal') && (
                                      <div data-tooltip-id={`is-fraud-${creditApplication.id}`}>
                                        <Flag color={COLORS.ERROR_500} size={14} weight="fill" />
                                      </div>
                                    )}
                                  <div className="w-full flex flex-wrap gap-1 md:gap-2 items-center justify-between">
                                    <div className="flex items-center gap-1 min-w-0 flex-1">
                                      <div
                                        className="text-xs truncate min-w-0"
                                        data-private={field?.isHashed ? true : false}>
                                        {field.fromField === 'CURRENCY'
                                          ? formatMoney(
                                              field.value,
                                              (field as any).currencyDecimalPlaces ?? 2
                                            )
                                          : resolveSupplierRefDisplayValue(
                                              field.id,
                                              field.value,
                                              supplierBranches
                                            )}
                                      </div>
                                      {field.id.includes('email-personal') &&
                                        isPreview &&
                                        creditApplication?.metaData?.ipAddress && (
                                          <div
                                            className="flex items-center shrink-0 cursor-help"
                                            data-tooltip-id={`email-verified-${creditApplication.id}`}>
                                            <CheckCircle
                                              size={14}
                                              weight="fill"
                                              style={{ color: COLORS.SUCCESS_500 }}
                                            />
                                            <ReactTooltip
                                              id={`email-verified-${creditApplication.id}`}
                                              place="top"
                                              variant="dark"
                                              opacity={'100%'}
                                              style={{
                                                borderRadius: '8px',
                                                fontSize: '12px',
                                                maxWidth: '260px',
                                                zIndex: 70,
                                              }}>
                                              Email ownership verified -- the applicant verified
                                              their email through an email verification link before
                                              they could submit this application
                                            </ReactTooltip>
                                          </div>
                                        )}
                                    </div>
                                    {field?.sendEmail &&
                                      field.status !== 'Submitted' &&
                                      onChangeTradeRefDetails && (
                                        <DillButton
                                          variant="ghost-blue"
                                          icon={<Edit size={14} />}
                                          height={22}
                                          width={22}
                                          onClick={() =>
                                            onChangeTradeRefDetails(
                                              field.id.includes('email') ? 'email' : 'company',
                                              field
                                            )
                                          }
                                        />
                                      )}
                                    {(field.id || '').endsWith('-address') &&
                                      field.addressVerification?.message &&
                                      isPreview && (
                                        <div
                                          className={`basis-full flex items-start gap-1.5 px-2 py-1 rounded-md border text-[10px] font-semibold ${
                                            field.addressVerification.verified
                                              ? 'SUCCESS_100-BG SUCCESS_500-CLR SUCCESS_500-BORDER'
                                              : 'WARNING_100-BG WARNING_600-CLR WARNING_500-BORDER'
                                          }`}>
                                          {field.addressVerification.verified ? (
                                            <CheckCircle
                                              size={12}
                                              weight="fill"
                                              className="mt-0.5 shrink-0"
                                            />
                                          ) : (
                                            <Warning
                                              size={12}
                                              weight="fill"
                                              className="mt-0.5 shrink-0"
                                            />
                                          )}
                                          <div className="flex flex-col gap-0.5">
                                            <span>{field.addressVerification.message}</span>
                                            {field.addressVerification.standardizedAddress && (
                                              <span className="font-normal GREY_500-CLR">
                                                {field.addressVerification.standardizedAddress}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                  {creditApplication?.isFraudulent &&
                                    isPreview &&
                                    field.id.includes('email-personal') && (
                                      <ReactTooltip
                                        id={`is-fraud-${creditApplication.id}`}
                                        place="top"
                                        variant="dark"
                                        opacity={'100%'}
                                        style={{
                                          display: 'flex',
                                          background: '#334155',
                                          width: '250px',
                                          zIndex: '70',
                                          borderRadius: '8px',
                                          alignItems: 'center',
                                          justifyItems: 'center',
                                          fontWeight: '600',
                                        }}>
                                        <span className="text-xs">
                                          Email domain does not match the provided company website
                                        </span>
                                      </ReactTooltip>
                                    )}
                                </div>
                                {field.id.includes('email-personal') &&
                                  isPreview &&
                                  creditApplication?.metaData && (
                                    <div className="flex flex-col gap-0.5 mt-0.5">
                                      {(creditApplication.metaData.location ||
                                        creditApplication.metaData.ipAddress) && (
                                        <div className="text-[10px] TEXT_SECONDARY-CLR">
                                          {creditApplication.metaData.location &&
                                            `Applied at ${creditApplication.metaData.location}`}
                                          {creditApplication.metaData.ipAddress &&
                                            ` from IP Address ${creditApplication.metaData.ipAddress}`}
                                        </div>
                                      )}
                                      {(() => {
                                        const flagReason = getIpLocationFlag(
                                          creditApplication.metaData.location,
                                          companyStateAbbr
                                        );
                                        return (
                                          flagReason && (
                                            <div
                                              className="flex items-center gap-1"
                                              style={{ color: COLORS.WARNING_600 }}>
                                              <Warning size={11} weight="fill" />
                                              <span className="text-[10px] font-medium">
                                                {flagReason}
                                              </span>
                                            </div>
                                          )
                                        );
                                      })()}
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {isPreview &&
                    field.id.includes('social-security') &&
                    process.env.REACT_APP_ENVIRONMENT !== 'production' && (
                      <div className="flex px-2 py-1 h-fit w-[120px] border SUCCESS_500-BORDER SUCCESS_100-BG SUCCESS_500-CLR items-center gap-1 rounded-md">
                        <div>
                          <CheckCircle size={14} weight="bold" />
                        </div>
                        <p className="text-xs font-semibold">SSN Verified</p>
                      </div>
                    )}
                  {field.id === 'customer-or-company-name' && middeskReport && isPreview && (
                    <div className="w-full mt-1 rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm">
                      {/* Header */}
                      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                        <CheckCircle size={13} weight="fill" className="GREY_500-CLR" />
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                          Business Verification
                        </span>
                      </div>

                      {/* Check rows */}
                      <div className="divide-y divide-gray-100">
                        {middeskReport?.nameMatch && (
                          <div className="flex items-start justify-between px-4 py-3 gap-4">
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-gray-700">Business Name</p>
                              {middeskReport.nameMatch.message && (
                                <p className="text-[11px] GREY_500-CLR mt-0.5 leading-relaxed">
                                  {middeskReport.nameMatch.message}
                                </p>
                              )}
                            </div>
                            <span
                              className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                                middeskReport.nameMatch.status.toLowerCase().includes('success')
                                  ? 'SUCCESS_100-BG SUCCESS_500-CLR SUCCESS_500-BORDER'
                                  : middeskReport.nameMatch.status.toLowerCase().includes('warning')
                                  ? 'WARNING_100-BG WARNING_500-CLR WARNING_500-BORDER'
                                  : 'ERROR_100-BG ERROR_500-CLR ERROR_500-BORDER'
                              }`}>
                              {middeskReport.nameMatch.status.toLowerCase().includes('success') ? (
                                <CheckCircle size={11} weight="bold" />
                              ) : (
                                <Warning size={11} weight="bold" />
                              )}
                              {middeskReport.nameMatch.label}
                            </span>
                          </div>
                        )}
                        {middeskReport?.sosMatch && (
                          <div className="flex items-start justify-between px-4 py-3 gap-4">
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-gray-700">
                                Secretary of State
                              </p>
                              {middeskReport.sosMatch.message && (
                                <p className="text-[11px] GREY_500-CLR mt-0.5 leading-relaxed">
                                  {middeskReport.sosMatch.message}
                                </p>
                              )}
                            </div>
                            <span
                              className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                                middeskReport.sosMatch.status.toLowerCase().includes('success')
                                  ? 'SUCCESS_100-BG SUCCESS_500-CLR SUCCESS_500-BORDER'
                                  : middeskReport.sosMatch.status.toLowerCase().includes('warning')
                                  ? 'WARNING_100-BG WARNING_500-CLR WARNING_500-BORDER'
                                  : 'ERROR_100-BG ERROR_500-CLR ERROR_500-BORDER'
                              }`}>
                              {middeskReport.sosMatch.status.toLowerCase().includes('success') ? (
                                <CheckCircle size={11} weight="bold" />
                              ) : (
                                <Warning size={11} weight="bold" />
                              )}
                              {middeskReport.sosMatch.label}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* State Filings */}
                      {middeskReport?.registration && middeskReport.registration.length > 0 && (
                        <div className="border-t border-gray-200">
                          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                              State Filings ({middeskReport.registration.length})
                            </span>
                          </div>
                          <div className="divide-y divide-gray-100">
                            {middeskReport.registration.map((item: any, ix: number) => (
                              <div key={`sos-${ix}`} className="px-4 py-4">
                                {/* Filing header */}
                                <div className="flex items-start justify-between mb-3">
                                  <div>
                                    <p className="text-xs font-bold text-gray-900">{item.state}</p>
                                    {item.name && (
                                      <p className="text-[11px] GREY_500-CLR mt-0.5">{item.name}</p>
                                    )}
                                  </div>
                                  <span
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${
                                      item.status?.toLowerCase() === 'active'
                                        ? 'SUCCESS_100-BG SUCCESS_500-CLR SUCCESS_500-BORDER'
                                        : 'ERROR_100-BG ERROR_500-CLR ERROR_500-BORDER'
                                    }`}>
                                    {item.status?.toLowerCase() === 'active' ? (
                                      <CheckCircle size={11} weight="bold" />
                                    ) : (
                                      <Warning size={11} weight="bold" />
                                    )}
                                    {item.status
                                      ? item.status.charAt(0).toUpperCase() +
                                        item.status.slice(1).toLowerCase()
                                      : ''}
                                  </span>
                                </div>

                                {/* Detail grid */}
                                <div className="grid grid-cols-3 gap-x-3 gap-y-2.5 mb-3">
                                  {item.entityType && (
                                    <div>
                                      <p className="text-[10px] GREY_500-CLR mb-0.5">Entity type</p>
                                      <p className="text-xs text-gray-800">{item.entityType}</p>
                                    </div>
                                  )}
                                  {item.jurisdiction && (
                                    <div>
                                      <p className="text-[10px] GREY_500-CLR mb-0.5">
                                        Jurisdiction
                                      </p>
                                      <p className="text-xs text-gray-800">{item.jurisdiction}</p>
                                    </div>
                                  )}
                                  {item.subStatus && (
                                    <div>
                                      <p className="text-[10px] GREY_500-CLR mb-0.5">Sub-status</p>
                                      <p className="text-xs text-gray-800">{item.subStatus}</p>
                                    </div>
                                  )}
                                  {item.statusDetails && (
                                    <div>
                                      <p className="text-[10px] GREY_500-CLR mb-0.5">
                                        Status details
                                      </p>
                                      <p className="text-xs text-gray-800">{item.statusDetails}</p>
                                    </div>
                                  )}
                                  {item.registrationDate && (
                                    <div>
                                      <p className="text-[10px] GREY_500-CLR mb-0.5">File date</p>
                                      <p className="text-xs text-gray-800">
                                        {item.registrationDate}
                                      </p>
                                    </div>
                                  )}
                                  {item.fileNumber && (
                                    <div>
                                      <p className="text-[10px] GREY_500-CLR mb-0.5">File number</p>
                                      <p className="text-xs text-gray-800">{item.fileNumber}</p>
                                    </div>
                                  )}
                                </div>

                                {/* Addresses */}
                                {item.addresses?.length > 0 && (
                                  <div className="mb-3">
                                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                                      Addresses
                                    </p>
                                    <div className="grid grid-cols-2 gap-2">
                                      {item.addresses.map((addr: string, ai: number) => (
                                        <p key={ai} className="text-xs text-gray-700">
                                          {addr}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* People */}
                                {(item.officers?.length > 0 || item.registeredAgent?.name) && (
                                  <div className="mb-3">
                                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                                      People
                                    </p>
                                    <div className="grid grid-cols-2 gap-2">
                                      {item.officers.map((officer: any, oi: number) => (
                                        <div key={oi}>
                                          <p className="text-[10px] GREY_500-CLR">Officer</p>
                                          <p className="text-xs text-gray-700">{officer.name}</p>
                                        </div>
                                      ))}
                                      {item.registeredAgent?.name && (
                                        <div>
                                          <p className="text-[10px] GREY_500-CLR">
                                            Registered agent
                                          </p>
                                          <p className="text-xs text-gray-700">
                                            {item.registeredAgent.name}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* SOS link */}
                                {item.source && (
                                  <a
                                    href={item.source}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs font-semibold PRIMARY_500-CLR hover:underline">
                                    {item.state} Secretary of State
                                    <ArrowCircleUpRight size={12} weight="bold" />
                                  </a>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {field.id === 'customer-or-company-name' &&
                    !creditApplication?.middeskBusinessId &&
                    creditApplication?.status !== 'OPENED' &&
                    creditApplication?.status !== 'SENT' &&
                    creditApplication?.status !== 'DRAFT' &&
                    process.env.REACT_APP_ENVIRONMENT === 'production' &&
                    isPreview && (
                      <DillButton
                        variant="secondary"
                        onClick={handleVerifySOS}
                        label="Verify SOS"
                        height="35px"
                        width="160px"
                        icon={<SealQuestion size={16} weight="fill" />}
                        isLoading={verifyingSOS}
                        isDisabled={verifyingSOS}
                      />
                    )}
                  {(field.id === 'company-ein' ||
                    (field.id.includes('ein-number') && !field.id.includes('trade-reference'))) &&
                    !creditApplication?.middeskBusinessId &&
                    creditApplication?.status !== 'OPENED' &&
                    creditApplication?.status !== 'SENT' &&
                    creditApplication?.status !== 'DRAFT' &&
                    process.env.REACT_APP_ENVIRONMENT === 'production' &&
                    isPreview && (
                      <DillButton
                        variant="secondary"
                        onClick={handleVerifySOS}
                        label="Verify EIN"
                        height="35px"
                        width="160px"
                        icon={<SealQuestion size={16} weight="fill" />}
                        isLoading={verifyingSOS}
                        isDisabled={verifyingSOS}
                      />
                    )}
                  {(field.id === 'company-ein' ||
                    (field.id.includes('ein-number') && !field.id.includes('trade-reference'))) &&
                    middeskReport &&
                    isPreview && (
                      <div className="w-full mt-1 rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm ml-4">
                        <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                          <CheckCircle size={13} weight="fill" className="GREY_500-CLR" />
                          <span className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                            EIN Verification
                          </span>
                        </div>
                        <div className="flex items-start justify-between px-4 py-3 gap-4">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-gray-700">Tax ID</p>
                            {middeskReport.tinMatch ? (
                              <>
                                {middeskReport.tinMatch.message && (
                                  <p className="text-[11px] GREY_500-CLR mt-0.5 leading-relaxed">
                                    {middeskReport.tinMatch.message}
                                  </p>
                                )}
                                {middeskReport?.tin?.name && (
                                  <p
                                    className="text-[11px] GREY_500-CLR mt-0.5"
                                    data-private={field?.isHashed ? true : false}>
                                    Found:{' '}
                                    <span className="font-semibold text-gray-700">
                                      {middeskReport.tin.name}
                                    </span>
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="text-[11px] GREY_500-CLR mt-0.5 leading-relaxed">
                                EIN verification is pending processing
                              </p>
                            )}
                          </div>
                          {middeskReport.tinMatch && (
                            <span
                              className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold border ${
                                middeskReport.tinMatch.status.toLowerCase().includes('success')
                                  ? 'SUCCESS_100-BG SUCCESS_500-CLR SUCCESS_500-BORDER'
                                  : 'ERROR_100-BG ERROR_500-CLR ERROR_500-BORDER'
                              }`}>
                              {middeskReport.tinMatch.status.toLowerCase().includes('success') ? (
                                <CheckCircle size={11} weight="bold" />
                              ) : (
                                <Warning size={11} weight="bold" />
                              )}
                              {middeskReport.tinMatch.label}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                </div>
              );
            })}
          </div>
        );
      });
    };

    const renderTradeReference = (fields: CreditApplicationFieldsValues[], index: number) => {
      const uniqueId = fields?.[index].uniqueId;
      const uploadedTradeReferenceResponse = tradeReferenceResponse?.[uniqueId] ?? null;

      const renderUploadSection = () => {
        return (
          <div className="flex flex-col gap-1 md:gap-2 mb-3">
            <DillButton
              label="Add reference check response manually"
              onClick={() => {
                setTradeReferenceManualResponse({ fields, index });
                setOpenTradeReferenceModal(true);
              }}
              variant="ghost-blue"
              isLoading={loadingTradeReferenceResponse === `loading_${uniqueId}`}
              isDisabled={loadingTradeReferenceResponse?.includes('loading')}
              className="w-fit"
            />
          </div>
        );
      };

      if (uploadedTradeReferenceResponse) {
        return (
          <div className="w-full mt-2 mb-2 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                Manual Trade Reference Response
              </p>
            </div>
            <div className="p-3">
              <div className="flex justify-start">
                <div className="flex gap-1 flex-col">
                  {uploadedTradeReferenceResponse?.textResponse && (
                    <div className="text-xs">{uploadedTradeReferenceResponse.textResponse}</div>
                  )}
                  {uploadedTradeReferenceResponse?.files &&
                    uploadedTradeReferenceResponse.files.length > 0 && (
                      <div className="flex flex-col gap-1 md:gap-2">
                        {uploadedTradeReferenceResponse.files.map((file, idx) => (
                          <div
                            key={`trade-reference-file-${idx}`}
                            className="flex gap-1 md:gap-2 items-center">
                            <div className="flex items-center gap-1 md:gap-2">
                              <DillButton
                                variant="icon-default"
                                width="25px"
                                height="25px"
                                onClick={() => {
                                  handleDownloadFile(file?.url ?? '', file?.name ?? '');
                                }}
                                icon={<DownloadSimple color={COLORS.GREY_500} size={14} />}
                              />
                              {['pdf', 'jpg', 'jpeg', 'png'].includes(file?.fileType ?? '') && (
                                <DillButton
                                  variant="icon-default"
                                  width="25px"
                                  height="25px"
                                  icon={<Eye color={COLORS.GREY_500} size={14} />}
                                  onClick={() => handlePreviewFile(file?.url ?? '', file?.fileType)}
                                />
                              )}
                            </div>
                            <div className="text-xs">{file?.name ?? ''}</div>
                            <DillButton
                              variant="icon-default"
                              width="25px"
                              height="25px"
                              icon={<Trash color={COLORS.ERROR_500} size={14} />}
                              onClick={() =>
                                onDeleteTradeReferenceFile &&
                                onDeleteTradeReferenceFile(uniqueId, file?.url ?? '')
                              }
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  <div className="w-full">
                    <DillButton
                      variant="ghost-blue"
                      icon={<Edit size={14} />}
                      height={22}
                      width={22}
                      onClick={() => {
                        setTradeReferenceManualResponse({ fields, index });
                        setOpenTradeReferenceModal(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }
      return renderUploadSection();
    };

    const renderTradeReferenceHistory = (fields: CreditApplicationFieldsValues[]) => {
      const foundEmail = fields.find((field) => field.id.includes('email'));

      const foundReferenceHistory = (tradeRefenceHistory ?? []).find(
        (tradeRefHistory) => tradeRefHistory.referenceId === foundEmail?.uniqueId
      );

      if (!foundReferenceHistory) return null;

      const excludedKeys = new Set([
        'updatedAt',
        'createdAt',
        'id',
        'supplierId',
        'creditApplicationId',
        'referenceId',
      ]);

      const tradeRefFieldOrder = [
        'email',
        'openSince',
        'termsOfPayment',
        'totalBalance',
        'currentDue',
        'amountOfPastDue',
        'creditLimit',
        'averageDaysBeyondTerms',
        'returnedChecks',
        'liensFiled',
        'badDebtWriteOffs',
        'comments',
      ];

      const sortedReferenceEntries = Object.entries(foundReferenceHistory)
        .filter(([refKey]) => !excludedKeys.has(refKey))
        .sort(([a], [b]) => {
          const aIndex = tradeRefFieldOrder.indexOf(a);
          const bIndex = tradeRefFieldOrder.indexOf(b);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });

      return (
        <div className="w-full mt-2 bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              Trade Reference Response
            </p>
          </div>
          <div className="grid grid-cols-3 gap-x-4 gap-y-3 p-4">
            {sortedReferenceEntries.map(([refKey, refVal], index) => {
              const CURRENCY_0DP = new Set(['creditLimit']);
              const CURRENCY_2DP = new Set([
                'totalBalance',
                'currentDue',
                'amountOfPastDue',
                'highCredit',
                'terms30',
                'terms60',
                'terms90',
                'terms120Plus',
              ]);
              const numVal = typeof refVal === 'number' ? refVal : Number(refVal);
              const displayVal =
                CURRENCY_0DP.has(refKey) && !isNaN(numVal)
                  ? formatMoney(numVal, 0)
                  : CURRENCY_2DP.has(refKey) && !isNaN(numVal)
                  ? formatMoney(numVal, 2)
                  : typeof refVal === 'string' ||
                    typeof refVal === 'number' ||
                    typeof refVal === 'boolean'
                  ? refVal
                  : refVal instanceof Date
                  ? moment(refVal).format('MM/DD/YYYY')
                  : '—';
              return (
                <div key={`ref-history-${refKey}-${index}`}>
                  <p className="text-[10px] GREY_500-CLR mb-0.5">{camelCaseToSentence(refKey)}</p>
                  <p className="text-xs font-medium text-gray-800">{displayVal}</p>
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div className="w-full space-y-3 mb-2 md:mb-4">
        <UpdateFileModal
          open={updateFileModal}
          handleClose={() => {
            setUpdateFileModal(false);
          }}
          creditApplicationId={currentCreditApplicationId}
          selectedField={selectedField}
          allStepFieldsValues={formData}
        />
        <TradeReferenceResponseModal
          open={openTradeReferenceModal}
          handleClose={() => setOpenTradeReferenceModal(false)}
          fields={tradeReferenceManualResponse?.fields ?? []}
          index={tradeReferenceManualResponse?.index ?? 0}
          onUploadTradeReferenceResponse={onUploadTradeReferenceResponse}
        />
        {selectedTransactionSummary && (
          <PlaidSummaryModal
            open={openPlaidSummaryModal}
            handleClose={() => setOpenPlaidSummaryModal(false)}
            summary={selectedTransactionSummary}
          />
        )}
        {selectedBankReference && (
          <BankReferenceResponseModal
            bankReference={selectedBankReference}
            open={openBankReferenceModal}
            handleClose={() => setOpenBankReferenceModal(false)}
            creditApplicationId={creditApplication?.id ?? ''}
            applicationHistoryId={applicationHistoryId}
          />
        )}
        <AppImagePreviewModal
          open={showPreviewImage}
          handleClose={() => {
            setShowPreviewImage(false);
            setAttachmentUrl('');
          }}
          title="Credit App Preview"
          url={attachmentUrl}
        />
        <AppPdfPreviewModal
          open={showPreviewPdf}
          handleClose={() => {
            setShowPreviewPdf(false);
            setAttachmentUrl('');
          }}
          invoice={''}
          pdfType="CREDIT_APPLICATION"
          pdfList={[
            {
              name: 'Credit Application',
              url: attachmentUrl,
              date: new Date(),
            },
          ]}
          handleDownloadPdf={() => {}}
          isSigned={false}
        />
        {Object.entries(groupedData).map(([stepName, stepGroups], index) => {
          let isBankRefRequired = false;
          if (stepName.toLowerCase().includes('bank reference')) {
            if (stepGroups['0']['0'][0] && stepGroups['0']['0'][0].isRequired) {
              isBankRefRequired = stepGroups['0']['0'][0].isRequired;
            }
          }
          const isBankStep = stepName.toLowerCase().includes('bank reference');
          if (isBankStep && !bankReference) return null;
          return (
            <div key={`step-${stepName}-${index}`}>
              {isBankStep && bankReference ? (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  {onGetPlaidDetails ? (
                    <div>
                      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                        <h2 className="text-sm font-bold text-gray-800 tracking-wide">
                          Bank Reference
                        </h2>
                      </div>

                      {bankReference.filter((br) => br.connectionType === 'plaid').length > 0 && (
                        <>
                          {bankReference
                            .filter(
                              (br) =>
                                br.bankDetails?.accessToken &&
                                br.connectionType?.toLowerCase().includes('plaid')
                            )
                            .map((bankRef, indx) => {
                              const plaidReference =
                                plaidMonthlyReportData.find((plaidRet) =>
                                  plaidRet?.name
                                    .toLowerCase()
                                    .includes(bankRef.bankDetails?.bankName.toLowerCase())
                                ) ??
                                plaidMonthlyReportData[indx] ??
                                plaidMonthlyReportData[0];
                              const bankMonths =
                                plaidReference?.historicalBalance?.map((item) => item.month) ?? [];
                              const bankDataSet = {
                                series: [
                                  {
                                    id: 'series-1',
                                    data:
                                      plaidReference?.historicalBalance?.map((s) => s.amount) ?? [],
                                    label:
                                      plaidReference?.name ??
                                      bankRef.bankDetails?.bankName ??
                                      'Balance',
                                  },
                                ],
                                xAxis: [
                                  { id: 'axis1', data: bankMonths, scaleType: 'point' as const },
                                ],
                                height: 300,
                                yAxis: [{ width: 80 }],
                              };
                              const bankMonthMsg =
                                bankMonths.length > 0 && bankMonths.length < 11
                                  ? `12 months of data pulled, only ${bankMonths.length} month${
                                      bankMonths.length > 1 ? 's' : ''
                                    } available.`
                                  : '';
                              return (
                                <div
                                  key={`plaid-ref-${indx}`}
                                  className="m-4 border border-gray-200 rounded-xl overflow-hidden">
                                  {/* Full-width header matching KCB style */}
                                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100">
                                    <p className="text-sm font-semibold text-gray-800">
                                      {`${
                                        bankRef.bankDetails?.bankName
                                      } ****${bankRef.bankDetails?.accountNumber?.slice(-4)}`}
                                    </p>
                                    {onRefreshPlaidDetails && wasRefreshedLastMonth && (
                                      <div className="flex items-center gap-2">
                                        <p className="text-xs GREY_500-CLR">
                                          Last Refreshed: {wasRefreshedLastMonth?.refreshedAt}
                                        </p>
                                        {wasRefreshedLastMonth.showRefreshButton && (
                                          <DillButton
                                            label="Refresh"
                                            variant="secondary"
                                            onClick={() => {
                                              onRefreshPlaidDetails();
                                            }}
                                            icon={<ArrowCounterClockwise size={16} />}
                                            width="120px"
                                            height="35px"
                                          />
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  {/* Two-column: bank details left, chart right */}
                                  <div className="flex w-full gap-4">
                                    <div className="flex flex-col gap-4 w-1/3 p-4">
                                      <DillButton
                                        label="View Bank Details"
                                        variant="primary"
                                        onClick={() =>
                                          onGetPlaidDetails(
                                            bankRef.bankDetails?.accessToken ?? '',
                                            'balance',
                                            ''
                                          )
                                        }
                                        width="200px"
                                        className="flex justify-center"
                                        isLoading={plaidLoadingStatus === 'balance'}
                                        isDisabled={
                                          plaidLoadingStatus === 'balance' ||
                                          plaidLoadingStatus.includes('_statements')
                                        }
                                      />
                                      <div>
                                        <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                                          Download Bank Statements
                                        </p>
                                        <DillDropdown
                                          isSearchable={false}
                                          containerClassName="h-[36px] px-1"
                                          value={selectedStatementMonth}
                                          placeholder="Select statement month"
                                          items={monthObjects.map((month) => ({
                                            label: month.name,
                                            value: month.value,
                                          }))}
                                          onSelectChange={(item) => {
                                            if (item?.value != null) {
                                              setSelectedStatementMonth(item.value);
                                              onGetPlaidDetails(
                                                bankRef.bankDetails?.accessToken ?? '',
                                                'statements',
                                                item.value
                                              );
                                            }
                                          }}
                                        />
                                      </div>
                                      {plaidReference &&
                                      plaidReference.loanTransactionsSummary &&
                                      plaidReference.loanTransactionsSummary.length > 0 ? (
                                        <PlaidSummary
                                          summary={plaidReference.loanTransactionsSummary}
                                          label="View Line of Credit Summary"
                                          onSelectSummary={(data) => {
                                            setSelectedTransactionSummary({
                                              ...data,
                                              bankName: `${
                                                bankRef.bankDetails?.bankName ?? ''
                                              } ****${bankRef.bankDetails?.accountNumber?.slice(
                                                -4
                                              )}`,
                                            });
                                            setOpenPlaidSummaryModal(true);
                                          }}
                                        />
                                      ) : (
                                        <div className="border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-400 bg-gray-50">
                                          No line of credit found in the last 12 months.
                                        </div>
                                      )}
                                      {plaidReference &&
                                      plaidReference.feesTransactionsSummary &&
                                      plaidReference.feesTransactionsSummary.length > 0 ? (
                                        <PlaidSummary
                                          summary={plaidReference.feesTransactionsSummary}
                                          label="View NSF/Overdraft Fee Summary"
                                          onSelectSummary={(data) => {
                                            setSelectedTransactionSummary({
                                              ...data,
                                              bankName: `${
                                                bankRef.bankDetails?.bankName ?? ''
                                              } ****${bankRef.bankDetails?.accountNumber?.slice(
                                                -4
                                              )}`,
                                            });
                                            setOpenPlaidSummaryModal(true);
                                          }}
                                        />
                                      ) : (
                                        <div className="border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-400 bg-gray-50">
                                          No NSF fees found in the last 12 months.
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex w-2/3 h-full flex-col gap-2 p-4">
                                      {plaidMonthlyReportData.length > 0 && (
                                        <div className="w-full">
                                          <p className="text-sm font-semibold mb-1 text-center">
                                            Historical Balance
                                          </p>
                                          <LineChart {...bankDataSet} />
                                          {bankMonthMsg && (
                                            <p className="text-xs mt-1 text-center">
                                              {bankMonthMsg}
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </>
                      )}
                      {bankReference.filter((br) => br.connectionType !== 'plaid').length > 0 && (
                        <ShowBankReferenceDetails
                          bankReferences={bankReference.filter(
                            (br) => br.connectionType !== 'plaid'
                          )}
                          isRequired={isBankRefRequired}
                          onAddBankReferenceResponse={(bkref) => {
                            setSelectedBankReference(bkref);
                            setOpenBankReferenceModal(true);
                          }}
                          onPreviewFile={handlePreviewFile}
                          onDownloadFile={handleDownloadFile}
                          isPreview={isPreview}
                          creditApplicationId={creditApplication?.id}
                          applicationHistoryId={applicationHistoryId}
                          className="px-4 py-4"
                        />
                      )}
                    </div>
                  ) : (
                    <ShowBankReferenceDetails
                      bankReferences={bankReference}
                      isRequired={isBankRefRequired}
                      onAddBankReferenceResponse={(bkref) => {
                        setSelectedBankReference(bkref);
                        setOpenBankReferenceModal(true);
                      }}
                      onPreviewFile={handlePreviewFile}
                      onDownloadFile={handleDownloadFile}
                      isPreview={isPreview}
                      creditApplicationId={creditApplication?.id}
                      applicationHistoryId={applicationHistoryId}
                      className="px-4 py-4"
                    />
                  )}
                </div>
              ) : (
                <div
                  key={`step-${stepName}-${index}`}
                  className={
                    isSignature
                      ? ''
                      : 'bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden'
                  }>
                  {!isSignature && (
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                      <h2 className="text-sm font-bold text-gray-800 tracking-wide">{stepName}</h2>
                    </div>
                  )}
                  {(() => {
                    const groupStepEntries = Object.entries(stepGroups).sort(
                      ([a], [b]) => Number(a) - Number(b)
                    );

                    const fieldPositionByFieldId: Record<string, number> = {};
                    groupStepEntries.forEach(([, groupStep], idx) => {
                      Object.values(groupStep)
                        .flat()
                        .forEach((f) => {
                          fieldPositionByFieldId[f.id] = idx;
                        });
                    });

                    const clusterIdByPosition: number[] = new Array(groupStepEntries.length).fill(
                      -1
                    );
                    {
                      let nextClusterId = 0;
                      let skipUntilIdx = -1;
                      groupStepEntries.forEach(([, groupStep], idx) => {
                        if (idx <= skipUntilIdx) return;
                        if (groupStep?.[0]?.[0]?.groupName) return;
                        const fieldsInEntry = Object.values(groupStep).flat();
                        const borderId = fieldsInEntry.find((f) => f.borderId)?.borderId;
                        if (!borderId) return;
                        const endIdx = fieldPositionByFieldId[borderId];
                        if (endIdx === undefined || endIdx < idx) return;
                        const clusterId = nextClusterId++;
                        for (let i = idx; i <= endIdx; i++) {
                          clusterIdByPosition[i] = clusterId;
                        }
                        skipUntilIdx = endIdx;
                      });
                    }

                    const mappedGroupSteps = groupStepEntries
                      .map(([groupsStepPos, groupStep], groupStepIndex) => {
                        const groupName = groupStep?.[0]?.[0]?.groupName ?? '';

                        if (isSignature) {
                          const allFields = Object.values(groupStep).flatMap((pos) =>
                            Object.values(pos).flat()
                          );
                          const hasVisible = allFields.some((f) => {
                            if (f.fromField !== 'INVITE_USER') return true;
                            const ft = f.type ?? '';
                            return (
                              pendingSignedData?.[ft] ||
                              !userSignatureTypes ||
                              userSignatureTypes.includes(ft)
                            );
                          });
                          if (!hasVisible) return null;
                        }

                        const matchedBorderGroupIdx = clusterIdByPosition[groupStepIndex] ?? -1;

                        const node = (
                          <div
                            key={`group-step-${stepName}-${groupsStepPos}-${index}`}
                            className={
                              matchedBorderGroupIdx === -1
                                ? 'px-4 py-2 border-b border-gray-100 last:border-b-0'
                                : 'px-2 py-1'
                            }>
                            {(() => {
                              const ocrUploadField = Object.values(groupStep)
                                .flat()
                                .find((f) => f.fromField === 'TRADE_REFERENCE');
                              const ocrValue = ocrUploadField?.value as {
                                name?: string;
                                url?: string;
                                fileType?: string;
                                extractedReferenceCount?: number;
                                extractedReferences?: ExtractedTradeReference[];
                              } | null;
                              if (!ocrUploadField || !ocrValue) return null;
                              const extractedCount = ocrValue.extractedReferenceCount ?? 0;
                              return (
                                <div className="mb-3 border border-gray-200 rounded-lg p-2">
                                  <div className="text-xs font-semibold mb-1">
                                    {ocrUploadField.name}
                                  </div>
                                  <div className="flex gap-1 md:gap-2 items-center">
                                    {isPreview && ocrValue.url && (
                                      <div className="flex items-center gap-1 md:gap-2">
                                        <DillButton
                                          variant="icon-default"
                                          width="25px"
                                          height="25px"
                                          onClick={() => {
                                            handleDownloadFile(
                                              ocrValue.url ?? '',
                                              ocrValue.name ?? ''
                                            );
                                          }}
                                          icon={
                                            <DownloadSimple color={COLORS.GREY_500} size={14} />
                                          }
                                        />
                                        {(ocrValue.fileType === 'pdf' ||
                                          ocrValue.fileType === 'jpg' ||
                                          ocrValue.fileType === 'jpeg' ||
                                          ocrValue.fileType === 'png') && (
                                          <DillButton
                                            variant="icon-default"
                                            width="25px"
                                            height="25px"
                                            icon={<Eye color={COLORS.GREY_500} size={14} />}
                                            onClick={() => {
                                              handlePreviewFile(
                                                ocrValue.url ?? '',
                                                ocrValue.fileType
                                              );
                                            }}
                                          />
                                        )}
                                      </div>
                                    )}
                                    <div className="text-xs truncate">{ocrValue.name ?? ''}</div>
                                  </div>
                                  <div
                                    className={`text-xs mt-1 flex items-center gap-1 ${
                                      extractedCount > 0 ? 'SUCCESS_500-CLR' : 'WARNING_500-CLR'
                                    }`}>
                                    {extractedCount > 0 ? (
                                      <CheckCircle size={14} weight="bold" />
                                    ) : (
                                      <Warning size={14} weight="bold" />
                                    )}
                                    {extractedCount > 0
                                      ? `Found ${extractedCount} trade reference${
                                          extractedCount === 1 ? '' : 's'
                                        }`
                                      : 'No trade references were found in this document.'}
                                  </div>
                                  {extractedCount > 0 && (
                                    <div className="mt-2 grid grid-cols-4 gap-2">
                                      {(ocrValue.extractedReferences ?? []).map(
                                        (extractedRef, refIdx) => {
                                          const fullAddress = [
                                            extractedRef.address,
                                            extractedRef.city,
                                            [extractedRef.state, extractedRef.zip]
                                              .filter(Boolean)
                                              .join(' '),
                                          ]
                                            .filter(Boolean)
                                            .join(', ');
                                          return (
                                            <div
                                              key={`ocr-extracted-${refIdx}`}
                                              className="flex flex-col gap-0.5 bg-gray-50 rounded px-2 py-1">
                                              <span className="text-xs font-semibold truncate">
                                                {extractedRef.companyName || 'Unnamed company'}
                                              </span>
                                              {extractedRef.contactName && (
                                                <span className="text-xs GREY_700-CLR truncate">
                                                  {extractedRef.contactName}
                                                </span>
                                              )}
                                              {extractedRef.email && (
                                                <span className="text-xs GREY_700-CLR truncate">
                                                  {extractedRef.email}
                                                </span>
                                              )}
                                              {extractedRef.phone && (
                                                <span className="text-xs GREY_700-CLR truncate">
                                                  {extractedRef.phone}
                                                </span>
                                              )}
                                              {extractedRef.accountNumber && (
                                                <span className="text-xs GREY_700-CLR truncate">
                                                  Acct# {extractedRef.accountNumber}
                                                </span>
                                              )}
                                              {fullAddress && (
                                                <span className="text-xs GREY_700-CLR truncate">
                                                  {fullAddress}
                                                </span>
                                              )}
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {Object.entries(groupStep)
                              .sort(([a], [b]) => Number(a) - Number(b))
                              .map(([groupPos, rawFields], indx, arry) => {
                                const fields = rawFields.filter(
                                  (f) => f.fromField !== 'TRADE_REFERENCE'
                                );
                                const emailTradeRef = fields.find(
                                  (f) =>
                                    f.groupedPosition === fields?.[index]?.groupedPosition &&
                                    f.id.includes('trade-reference-company-email')
                                );
                                const referenceHistory = emailTradeRef?.referenceHistoryDate;
                                // A stored response means the reference answered, even if the
                                // field's status was reset or never updated (e.g. a follow-up
                                // sent after the response, or a submit that failed part-way).
                                const hasReferenceResponse = (tradeRefenceHistory ?? []).some(
                                  (response) => response.referenceId === emailTradeRef?.uniqueId
                                );
                                const referenceStatus = hasReferenceResponse
                                  ? 'Submitted'
                                  : emailTradeRef?.status;
                                return (
                                  <div
                                    key={`group-${groupPos}-${stepName}-${groupsStepPos}-${index}`}
                                    className={`${
                                      fields.length > 1 && indx !== arry.length - 1
                                        ? 'border-b mb-3'
                                        : ''
                                    } ${groupName ? 'border border-gray-100 rounded-lg' : ''}`}>
                                    {groupName && (
                                      <div className="flex items-center justify-between mb-3 border-b border-gray-100 pr-2 py-1">
                                        <div
                                          className={`flex items-center gap-2 ${
                                            groupName ? 'px-2 pt-2' : ''
                                          }`}>
                                          {arry.length > 1 && (
                                            <span className="flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 text-[10px] font-bold text-gray-500 shrink-0">
                                              {indx + 1}
                                            </span>
                                          )}
                                          <h3 className="text-xs font-bold text-gray-700 text-nowrap">
                                            {groupName}
                                          </h3>
                                        </div>
                                        {groupStep?.[0]?.[0]?.sendEmail &&
                                          onFollowUp &&
                                          onSendEmail && (
                                            <div className="flex w-full flex-row-reverse items-center gap-2 md:gap-3">
                                              {referenceStatus !== 'Submitted' &&
                                                referenceStatus !== 'Pending' && (
                                                  <div className="flex items-center gap-2 md:gap-4">
                                                    {referenceStatus && (
                                                      <div
                                                        data-tooltip-id={`reference-status-${emailTradeRef?.uniqueId}`}
                                                        className={`flex items-center gap-1 md:gap-2 text-xs px-3 py-1 rounded-full font-semibold ${
                                                          referenceStatus === 'Delivered' ||
                                                          referenceStatus === 'Requested'
                                                            ? 'WARNING_500-CLR WARNING_100-BG'
                                                            : 'ERROR_500-CLR ERROR_100-BG'
                                                        }`}>
                                                        {referenceStatus === 'Delivered' ||
                                                        referenceStatus === 'Requested' ? (
                                                          <Timer size={16} weight="bold" />
                                                        ) : (
                                                          <Warning size={16} weight="bold" />
                                                        )}
                                                        <div className="text-xs">{`${
                                                          referenceStatus === 'Delivered'
                                                            ? 'Requested'
                                                            : referenceStatus
                                                        }`}</div>
                                                      </div>
                                                    )}

                                                    {referenceStatus && (
                                                      <ReactTooltip
                                                        id={`reference-status-${emailTradeRef?.uniqueId}`}
                                                        place="bottom"
                                                        variant="dark"
                                                        opacity={'100%'}
                                                        style={{
                                                          background: '#222',
                                                          width: '250px',
                                                          zIndex: 99999,
                                                        }}>
                                                        <span className="text-xs">
                                                          {`Your email was ${referenceStatus?.toLowerCase()}.`}
                                                        </span>
                                                      </ReactTooltip>
                                                    )}
                                                    {referenceStatus && emailTradeRef?.value && (
                                                      <div
                                                        className="flex items-center gap-1 md:gap-2 text-xs PRIMARY_500-CLR font-semibold cursor-pointer"
                                                        onClick={() => {
                                                          if (onFollowUp) {
                                                            onFollowUp(fields, indx);
                                                          }
                                                        }}>
                                                        <div className="text-xs">Follow Up</div>
                                                        <ArrowCircleUpRight size={16} />
                                                      </div>
                                                    )}
                                                    {emailTradeRef?.value && (
                                                      <div
                                                        data-tooltip-id={`copy-reference-link-${emailTradeRef?.uniqueId}`}>
                                                        <DillButton
                                                          onClick={() =>
                                                            handleCopyReferenceLink(
                                                              emailTradeRef?.value as string
                                                            )
                                                          }
                                                          variant="icon-blue"
                                                          icon={<Copy size={14} weight="bold" />}
                                                          height={28}
                                                          width={28}
                                                        />
                                                      </div>
                                                    )}
                                                    {emailTradeRef?.value && (
                                                      <ReactTooltip
                                                        id={`copy-reference-link-${emailTradeRef?.uniqueId}`}
                                                        place="bottom"
                                                        variant="dark"
                                                        opacity={'100%'}
                                                        style={{
                                                          background: '#222',
                                                          zIndex: 99999,
                                                        }}>
                                                        <span className="text-xs">
                                                          Copy reference link
                                                        </span>
                                                      </ReactTooltip>
                                                    )}
                                                  </div>
                                                )}
                                              {referenceStatus === 'Submitted' && (
                                                <div className="flex items-center gap-2 md:gap-4">
                                                  <div className="flex items-center gap-1 md:gap-2 text-xs SUCCESS_500-CLR SUCCESS_100-BG px-3 py-1 rounded-full font-semibold">
                                                    <CheckCircle size={16} />
                                                    <div className="text-xs">Submitted</div>
                                                  </div>
                                                </div>
                                              )}
                                              {(referenceStatus === 'Pending' ||
                                                !referenceStatus) &&
                                                emailTradeRef?.value && (
                                                  <DillButton
                                                    label="Send Email"
                                                    onClick={() => {
                                                      if (onSendEmail) {
                                                        onSendEmail(fields, indx);
                                                      }
                                                    }}
                                                    variant="secondary"
                                                    icon={
                                                      <Mail
                                                        className={COLORS.PRIMARY_500}
                                                        size={16}
                                                      />
                                                    }
                                                    height="35px"
                                                    width="140px"
                                                    isLoading={
                                                      loadingButton === `sendEmail_${indx}`
                                                    }
                                                    isDisabled={
                                                      loadingButton === `sendEmail_${indx}`
                                                    }
                                                  />
                                                )}
                                              {referenceHistory && (
                                                <TradeReferenceHistory
                                                  referenceHistory={referenceHistory}
                                                  buttonStyles={{
                                                    height: '30px',
                                                  }}
                                                  buttonType="TERTIARY"
                                                  buttonTitle="View History"
                                                />
                                              )}
                                            </div>
                                          )}
                                      </div>
                                    )}
                                    <div className={`${groupName ? 'px-2 pb-2' : ''}`}>
                                      {renderFieldData(fields)}
                                      {tradeRefenceHistory && renderTradeReferenceHistory(fields)}
                                      {fields?.[index]?.sendEmail &&
                                        onUploadTradeReferenceResponse &&
                                        renderTradeReference(fields, index)}

                                      {groupStep?.[0]?.[0]?.groupName &&
                                        groupStep?.[0]?.[0]?.sendEmail &&
                                        onAddTradeReference &&
                                        indx === arry.length - 1 && (
                                          <DillButton
                                            label={`Add ${groupStep?.[0]?.[0]?.groupName}`}
                                            variant="primary"
                                            onClick={() => {
                                              onAddTradeReference(fields);
                                            }}
                                            icon={<PlusCircle color={COLORS.WHITE} size={12} />}
                                            height="30px"
                                          />
                                        )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        );

                        return { node, matchedBorderGroupIdx };
                      })
                      .filter(Boolean) as {
                      node: JSX.Element;
                      matchedBorderGroupIdx: number;
                    }[];

                    const output: JSX.Element[] = [];
                    let gsi = 0;
                    while (gsi < mappedGroupSteps.length) {
                      const { matchedBorderGroupIdx } = mappedGroupSteps[gsi];
                      if (matchedBorderGroupIdx === -1) {
                        output.push(mappedGroupSteps[gsi].node);
                        gsi++;
                        continue;
                      }
                      const clusterStart = gsi;
                      while (
                        gsi < mappedGroupSteps.length &&
                        mappedGroupSteps[gsi].matchedBorderGroupIdx === matchedBorderGroupIdx
                      )
                        gsi++;
                      const isLastCluster = gsi === mappedGroupSteps.length;
                      output.push(
                        <div
                          key={`bordered-group-step-wrap-${matchedBorderGroupIdx}-${clusterStart}`}
                          className={`px-4 py-2 ${
                            isLastCluster ? '' : 'border-b border-gray-100'
                          }`}>
                          <div className="border border-gray-200 rounded-lg p-2 flex flex-col gap-2">
                            {mappedGroupSteps.slice(clusterStart, gsi).map((m, rowIdx, rowsArr) => (
                              <div
                                key={`bordered-row-${matchedBorderGroupIdx}-${clusterStart}-${rowIdx}`}
                                className={
                                  rowIdx !== rowsArr.length - 1
                                    ? 'border-b border-gray-100 pb-2'
                                    : ''
                                }>
                                {m.node}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return output;
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
);
